// TeamManagementScreen.js — Full-featured team management with API integration
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, TextInput, Alert, Modal,
  FlatList, ActivityIndicator, ScrollView, RefreshControl, KeyboardAvoidingView,
  Platform, Share, Linking, Dimensions, Animated,

} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

const { width } = Dimensions.get('window');
const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

// ── API ──────────────────────────────────────────────────────────────────────
const api = async (path, method = 'GET', body = null) => {
  const token = await AsyncStorage.getItem('auth_token');
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

// ── Constants ────────────────────────────────────────────────────────────────
const TODAY = new Date().toISOString().split('T')[0];
const PALETTE = ['#0e4e96','#1976D2','#388E3C','#F57C00','#7B1FA2','#D32F2F','#0097A7','#5D4037','#C2185B','#455A64'];
const CONTRACT_TYPES = ['Permanent','Fixed-term','Contractor','Part-time','Intern','Zero-hours'];
const STATUS_LIST    = ['active','inactive','on_leave','remote'];
const MEETING_TYPES  = ['one-time','recurring','daily','weekly','monthly'];
const PERF_LABELS    = ['No Data','Poor','Below Average','Average','Good','Excellent'];
const DEPT_ROLES     = ['Engineering','Sales','Marketing','HR','Finance','Operations','Design','Support','Legal','Executive'];
const SORT_OPTIONS   = [
  { key: 'name',        label: 'Name'        },
  { key: 'performance', label: 'Performance' },
  { key: 'joinDate',    label: 'Join Date'   },
  { key: 'department',  label: 'Department'  },
  { key: 'status',      label: 'Status'      },
];

// ── Pure helpers ─────────────────────────────────────────────────────────────
const initials = (name = '') => (name.split(' ').slice(0, 2).map(w => (w[0] || '').toUpperCase()).join('')) || '?';
const colorFor  = (str = '') => PALETTE[str.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % PALETTE.length];
const perfColor = (v) => v >= 90 ? '#388E3C' : v >= 70 ? '#1976D2' : v >= 50 ? '#F57C00' : '#D32F2F';
const fmtCurrency = (n) => n == null ? '—' : `£${Number(n).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const statusMeta = (s) => ({
  active:   { bg: '#E8F5E9', fg: '#2E7D32', label: 'Active'   },
  inactive: { bg: '#FFEBEE', fg: '#C62828', label: 'Inactive' },
  on_leave: { bg: '#FFF3E0', fg: '#E65100', label: 'On Leave' },
  remote:   { bg: '#E3F2FD', fg: '#1565C0', label: 'Remote'   },
}[s] || { bg: '#F5F5F5', fg: '#616161', label: s });

// ── Shared sub-components ────────────────────────────────────────────────────
const Avatar = ({ name, size = 44, color, online }) => {
  const bg = color || colorFor(name);
  return (
    <View style={{ width: size, height: size, borderRadius: size / 2, backgroundColor: bg,
      alignItems: 'center', justifyContent: 'center' }}>
      <Text style={{ color: '#fff', fontSize: size * 0.36, fontWeight: '800' }}>{initials(name)}</Text>
      {online !== undefined && (
        <View style={{ position: 'absolute', bottom: 1, right: 1, width: size * 0.28, height: size * 0.28,
          borderRadius: size * 0.14, backgroundColor: online ? '#4CAF50' : '#9E9E9E',
          borderWidth: 2, borderColor: '#fff' }} />
      )}
    </View>
  );
};

const Badge = ({ status }) => {
  const m = statusMeta(status);
  return (
    <View style={{ backgroundColor: m.bg, borderRadius: 10, paddingHorizontal: 8, paddingVertical: 2 }}>
      <Text style={{ fontSize: 11, fontWeight: '700', color: m.fg }}>{m.label}</Text>
    </View>
  );
};

const PerfBar = ({ value = 0, height = 6, showLabel = false }) => {
  const c = perfColor(value);
  return (
    <View>
      {showLabel && (
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 }}>
          <Text style={{ fontSize: 11, color: '#999' }}>Performance</Text>
          <Text style={{ fontSize: 11, fontWeight: '700', color: c }}>{value}%</Text>
        </View>
      )}
      <View style={{ height, backgroundColor: '#ECEFF1', borderRadius: height / 2, overflow: 'hidden' }}>
        <View style={{ width: `${Math.min(Math.max(value, 0), 100)}%`, height: '100%', backgroundColor: c, borderRadius: height / 2 }} />
      </View>
    </View>
  );
};

const FieldInput = ({ label, value, onChange, placeholder, numeric, multiline, required, info }) => (
  <View style={{ marginBottom: 14 }}>
    <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 5 }}>
      <Text style={{ fontSize: 13, fontWeight: '600', color: '#444' }}>{label}{required ? <Text style={{ color: '#D32F2F' }}> *</Text> : ''}</Text>
      {info ? <Text style={{ fontSize: 11, color: '#90A4AE', marginLeft: 6 }}>{info}</Text> : null}
    </View>
    <TextInput
      style={[St.field, multiline && { height: 80, textAlignVertical: 'top', paddingTop: 10 }]}
      value={value} onChangeText={onChange} placeholder={placeholder || label}
      placeholderTextColor="#C0C0C0" keyboardType={numeric ? 'decimal-pad' : 'default'}
      multiline={multiline} autoCorrect={false} autoCapitalize="words" />
  </View>
);

const SectionHeader = ({ title, count, onAdd, addLabel = 'Add' }) => (
  <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
      <Text style={St.sectionTitle}>{title}</Text>
      {count != null && <View style={St.countPill}><Text style={St.countPillText}>{count}</Text></View>}
    </View>
    {onAdd && (
      <TouchableOpacity onPress={onAdd} style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
        <Ionicons name="add-circle" size={22} color="#0e4e96" />
        <Text style={{ fontSize: 13, color: '#0e4e96', fontWeight: '600' }}>{addLabel}</Text>
      </TouchableOpacity>
    )}
  </View>
);

const Chip = ({ label, active, onPress, color }) => (
  <TouchableOpacity onPress={onPress}
    style={[St.chip, active && { backgroundColor: color || '#0e4e96', borderColor: color || '#0e4e96' }]}>
    <Text style={[St.chipText, active && { color: '#fff' }]}>{label}</Text>
  </TouchableOpacity>
);

const Divider = () => <View style={{ height: 1, backgroundColor: '#F5F5F5', marginVertical: 6 }} />;

const EmptyState = ({ icon, title, sub, onAction, actionLabel }) => (
  <View style={St.emptyWrap}>
    <View style={St.emptyIconWrap}><Ionicons name={icon} size={44} color="#CFD8DC" /></View>
    <Text style={St.emptyTitle}>{title}</Text>
    {sub ? <Text style={St.emptySub}>{sub}</Text> : null}
    {onAction && (
      <TouchableOpacity style={St.emptyBtn} onPress={onAction}>
        <Ionicons name="add" size={16} color="#fff" />
        <Text style={St.emptyBtnText}>{actionLabel || 'Add'}</Text>
      </TouchableOpacity>
    )}
  </View>
);

const InfoRow = ({ icon, label, value, onPress }) => {
  if (!value) return null;
  const Inner = (
    <View style={St.infoRow}>
      <View style={St.infoIcon}><Ionicons name={icon} size={16} color="#0e4e96" /></View>
      <Text style={St.infoLabel}>{label}</Text>
      <Text style={St.infoValue} numberOfLines={2}>{value}</Text>
      {onPress && <Ionicons name="chevron-forward" size={14} color="#B0BEC5" />}
    </View>
  );
  return onPress ? <TouchableOpacity onPress={onPress}>{Inner}</TouchableOpacity> : Inner;
};

// ── Main Screen ──────────────────────────────────────────────────────────────
export default function TeamManagementScreen({ navigation }) {
  const [companyId, setCompanyId] = useState(null);

  // Data
  const [members,     setMembers]     = useState([]);
  const [departments, setDepartments] = useState([]);
  const [meetings,    setMeetings]    = useState([]);

  // UI
  // ── Company logo ──────────────────────────────────────────────────────────
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [activeTab,   setActiveTab]   = useState('overview');
  const [loading,     setLoading]     = useState(true);
  const [refreshing,  setRefreshing]  = useState(false);
  const [saving,      setSaving]      = useState(false);

  // Filters / search
  const [searchQ,      setSearchQ]      = useState('');
  const [filterDept,   setFilterDept]   = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterContract, setFilterContract] = useState('');
  const [sortKey,      setSortKey]      = useState('name');
  const [sortDir,      setSortDir]      = useState('asc');
  const [showFilters,  setShowFilters]  = useState(false);
  const [meetSearch,   setMeetSearch]   = useState('');
  const [meetFilter,   setMeetFilter]   = useState('');

  // Bulk select
  const [bulkMode,     setBulkMode]     = useState(false);
  const [selected,     setSelected]     = useState(new Set());

  // Modals
  const [memberModal,  setMemberModal]  = useState(false);
  const [detailModal,  setDetailModal]  = useState(false);
  const [meetModal,    setMeetModal]    = useState(false);
  const [deptModal,    setDeptModal]    = useState(false);
  const [bulkModal,    setBulkModal]    = useState(false);

  // Editing targets
  const [editMember,  setEditMember]  = useState(null);
  const [viewMember,  setViewMember]  = useState(null);
  const [editMeeting, setEditMeeting] = useState(null);
  const [editDept,    setEditDept]    = useState(null);

  // Forms
  const [mf,  setMf]  = useState(blankMember());
  const [mtf, setMtf] = useState(blankMeeting());
  const [df,  setDf]  = useState(blankDept());
  const [meetParticipants, setMeetParticipants] = useState([]);

  // Detail tab
  const [detailTab, setDetailTab] = useState('info');

  // Integration state
  const [companyProfile, setCompanyProfile] = useState(null);

  // Performance inline update
  const [perfModal,    setPerfModal]    = useState(false);
  const [perfTarget,   setPerfTarget]   = useState(null); // member being updated
  const [perfValue,    setPerfValue]    = useState('');

  // Attendance tracking
  const [attendance,   setAttendance]   = useState({}); // { [memberId]: 'in'|'out'|'late' }
  const [attendModal,  setAttendModal]  = useState(false);

  // Member notes quick-add
  const [noteModal,    setNoteModal]    = useState(false);
  const [noteTarget,   setNoteTarget]   = useState(null);
  const [noteText,     setNoteText]     = useState('');

  // Expanded member card
  const [expandedCard, setExpandedCard] = useState(null);

  const syncLock = useRef(false);

  function blankMember() {
    return {
      name: '', role: '', department: '', email: '', phone: '', status: 'active',
      contract_type: 'Permanent', salary: '', manager: '', territory: '',
      skills: '', certifications: '', languages: 'English',
      notes: '', join_date: TODAY, target: '',
    };
  }
  function blankMeeting() {
    return {
      title: '', meeting_date: TODAY, time: '09:00', duration_minutes: '60',
      type: 'one-time', location: '', agenda: '', notes: '',
    };
  }
  function blankDept() {
    return { name: '', head: '', description: '', color: PALETTE[0], budget: '' };
  }

  // ── Bootstrap ────────────────────────────────────────────────────────────────
  const loadCompanyProfile = async () => {
    try {
      loadCompanyProfile();
      const cached = await AsyncStorage.getItem('company_profile');
      if (cached) {
        const cp = JSON.parse(cached);
        setCompanyName(cp.company_name || cp.name || '');
        const l = cp.logo || cp.logo_url || cp.company_logo || cp.banner_image_url || null;
        if (l) setCompanyLogo(l);
      }
      const cid = await AsyncStorage.getItem('company_id');
      if (!cid) return;
      const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
      const res = await fetch(`https://api.jobeng.ai/api/data/company_profiles?company_id=${cid}`,
        { headers: { 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef', ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
      if (!res.ok) return;
      const r = await res.json();
      const cp = Array.isArray(r) ? r[0] : (r.data?.[0] ?? r.data ?? r);
      if (cp) {
        setCompanyName(cp.company_name || cp.name || '');
        const l = cp.logo || cp.logo_url || cp.company_logo || cp.banner_image_url || null;
        if (l) setCompanyLogo(l);
        await AsyncStorage.setItem('company_profile', JSON.stringify(cp));
      }
    } catch {}
  };

  useEffect(() => {
    AsyncStorage.getItem('company_id').then(id => { if (id) setCompanyId(id); });
  }, []);

  useEffect(() => {
    if (companyId) fetchAll(true);
  }, [companyId]);

  useEffect(() => {
    const unsub = navigation.addListener('focus', () => { if (companyId) fetchAll(false); });
    return unsub;
  }, [navigation, companyId]);

  // ── Data loading ─────────────────────────────────────────────────────────────
  const fetchAll = async (spinner = true) => {
    if (syncLock.current) return;
    syncLock.current = true;
    if (spinner) setLoading(true);
    try { await Promise.all([fetchMembers(), fetchDepartments(), fetchMeetings()]); }
    finally { setLoading(false); syncLock.current = false; }
  };

  const fetchMembers = async () => {
    try {
      const r = await api(`/api/data/team_members?company_id=${companyId}`);
      setMembers(Array.isArray(r) ? r : (r.data ?? r.team_members ?? []));
    } catch (e) { console.error('fetchMembers', e); }
  };

  const fetchDepartments = async () => {
    try {
      const r = await api(`/api/data/team_departments?company_id=${companyId}`);
      setDepartments(Array.isArray(r) ? r : (r.data ?? r.departments ?? []));
    } catch (e) { console.error('fetchDepartments', e); }
  };

  const fetchMeetings = async () => {
    try {
      const r = await api(`/api/data/team_meetings?company_id=${companyId}&meeting_date>=${TODAY}`);
      const data = Array.isArray(r) ? r : (r.data ?? r.meetings ?? []);
      setMeetings(data.sort((a, b) => (a.meeting_date + (a.time || '')).localeCompare(b.meeting_date + (b.time || ''))));
    } catch (e) { console.error('fetchMeetings', e); }
  };

  const onRefresh = async () => { setRefreshing(true); await fetchAll(false); setRefreshing(false); };

  // Load company profile from cache for navigation
  useEffect(() => {
    AsyncStorage.getItem('company_profile')
      .then(v => { if (v) try { setCompanyProfile(JSON.parse(v)); } catch {} })
      .catch(() => {});
  }, []);

  // ── Performance update ────────────────────────────────────────────────────────
  const openPerfUpdate = (m) => {
    setPerfTarget(m);
    setPerfValue(String(m.performance_score || m.performance || ''));
    setPerfModal(true);
  };

  const savePerfUpdate = async () => {
    if (!perfTarget) return;
    const score = Math.min(100, Math.max(0, parseInt(perfValue) || 0));
    setSaving(true);
    try {
      await api(`/api/data/team_members/${perfTarget.id}`, 'PUT', {
        ...perfTarget, company_id: companyId, performance_score: score,
      });
      setMembers(prev => prev.map(m => m.id === perfTarget.id ? { ...m, performance_score: score, performance: score } : m));
      if (viewMember?.id === perfTarget.id) setViewMember(v => ({ ...v, performance_score: score }));
      setPerfModal(false);
    } catch { Alert.alert('Error', 'Could not update performance.'); }
    finally { setSaving(false); }
  };

  // ── Quick note ────────────────────────────────────────────────────────────────
  const openNoteAdd = (m) => {
    setNoteTarget(m);
    setNoteText(m.notes || '');
    setNoteModal(true);
  };

  const saveNote = async () => {
    if (!noteTarget) return;
    setSaving(true);
    try {
      await api(`/api/data/team_members/${noteTarget.id}`, 'PUT', {
        ...noteTarget, company_id: companyId, notes: noteText.trim(),
      });
      setMembers(prev => prev.map(m => m.id === noteTarget.id ? { ...m, notes: noteText.trim() } : m));
      setNoteModal(false);
    } catch { Alert.alert('Error', 'Could not save note.'); }
    finally { setSaving(false); }
  };

  // ── Attendance mark ───────────────────────────────────────────────────────────
  const markAttendance = async (memberId, status) => {
    setAttendance(prev => ({ ...prev, [memberId]: status }));
    // Persist via member status mapping
    const m = members.find(x => x.id === memberId);
    if (!m) return;
    const newStatus = status === 'out' ? 'inactive' : status === 'late' ? 'active' : 'active';
    try {
      await api(`/api/data/team_members/${memberId}`, 'PUT', {
        ...m, company_id: companyId, status: newStatus, checked_in: status === 'in',
      });
    } catch {}
  };

  // ── Member CRUD ──────────────────────────────────────────────────────────────
  const openAddMember = (prefill = {}) => {
    setEditMember(null);
    setMf({ ...blankMember(), ...prefill });
    setMemberModal(true);
  };

  const openEditMember = (m) => {
    setEditMember(m);
    setMf({
      name: m.name || '', role: m.role || '', department: m.department || '',
      email: m.email || '', phone: m.phone || '', status: m.status || 'active',
      contract_type: m.contract_type || 'Permanent', salary: m.salary ? String(m.salary) : '',
      manager: m.manager || '', territory: m.territory || '',
      skills: Array.isArray(m.skills) ? m.skills.join(', ') : (m.skills || ''),
      certifications: Array.isArray(m.certifications) ? m.certifications.join(', ') : (m.certifications || ''),
      languages: Array.isArray(m.languages) ? m.languages.join(', ') : (m.languages || 'English'),
      notes: m.notes || '', join_date: m.join_date || m.joinDate || TODAY,
      target: m.target ? String(m.target) : '',
    });
    setDetailModal(false);
    setMemberModal(true);
  };

  const saveMember = async () => {
    if (!mf.name.trim()) { Alert.alert('Required', 'Full name is required.'); return; }
    if (!mf.email.trim()) { Alert.alert('Required', 'Email is required.'); return; }
    setSaving(true);
    try {
      const payload = {
        company_id:    companyId,
        name:          mf.name.trim(),
        role:          mf.role.trim(),
        department:    mf.department.trim(),
        email:         mf.email.trim().toLowerCase(),
        phone:         mf.phone.trim(),
        status:        mf.status,
        contract_type: mf.contract_type,
        salary:        mf.salary ? parseFloat(mf.salary) : null,
        manager:       mf.manager.trim(),
        territory:     mf.territory.trim(),
        skills:        mf.skills.split(',').map(s => s.trim()).filter(Boolean),
        certifications:mf.certifications.split(',').map(s => s.trim()).filter(Boolean),
        languages:     mf.languages.split(',').map(s => s.trim()).filter(Boolean),
        notes:         mf.notes.trim(),
        join_date:     mf.join_date,
        target:        mf.target ? parseFloat(mf.target) : null,
      };
      if (editMember?.id) {
        await api(`/api/data/team_members/${editMember.id}`, 'PUT', payload);
        Alert.alert('Updated', `${payload.name} has been updated.`);
      } else {
        await api('/api/data/team_members', 'POST', payload);
        Alert.alert('Added', `${payload.name} has been added to the team.`);
      }
      setMemberModal(false);
      await fetchMembers();
    } catch { Alert.alert('Error', 'Could not save. Please try again.'); }
    finally { setSaving(false); }
  };

  const deleteMember = (m) => {
    Alert.alert('Remove Member', `Remove ${m.name} from the team? This cannot be undone.`,
      [{ text: 'Cancel', style: 'cancel' },
       { text: 'Remove', style: 'destructive', onPress: async () => {
          try {
            await api(`/api/data/team_members/${m.id}`, 'DELETE');
            setDetailModal(false);
            await fetchMembers();
            Alert.alert('Removed', `${m.name} has been removed.`);
          } catch { Alert.alert('Error', 'Could not remove member.'); }
       }}]);
  };

  const toggleStatus = async (m) => {
    const next = { active: 'inactive', inactive: 'active', on_leave: 'active', remote: 'active' }[m.status] || 'active';
    try {
      await api(`/api/data/team_members/${m.id}`, 'PUT', { ...m, company_id: companyId, status: next });
      await fetchMembers();
      if (viewMember?.id === m.id) setViewMember(v => ({ ...v, status: next }));
    } catch { Alert.alert('Error', 'Could not update status.'); }
  };

  // ── Bulk operations ──────────────────────────────────────────────────────────
  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(filteredMembers.map(m => m.id)));
  const clearSelect = () => { setSelected(new Set()); setBulkMode(false); };

  const bulkSetStatus = async (status) => {
    setSaving(true);
    try {
      await Promise.all([...selected].map(id => {
        const m = members.find(x => x.id === id);
        return m ? api(`/api/data/team_members/${id}`, 'PUT', { ...m, company_id: companyId, status }) : Promise.resolve();
      }));
      setBulkModal(false);
      clearSelect();
      await fetchMembers();
      Alert.alert('Done', `${selected.size} member(s) updated to ${status}.`);
    } catch { Alert.alert('Error', 'Some updates failed.'); }
    finally { setSaving(false); }
  };

  const bulkDelete = () => {
    Alert.alert('Delete Members', `Delete ${selected.size} member(s)? This cannot be undone.`,
      [{ text: 'Cancel', style: 'cancel' },
       { text: 'Delete All', style: 'destructive', onPress: async () => {
          setSaving(true);
          try {
            await Promise.all([...selected].map(id => api(`/api/data/team_members/${id}`, 'DELETE')));
            setBulkModal(false);
            clearSelect();
            await fetchMembers();
          } catch { Alert.alert('Error', 'Some deletions failed.'); }
          finally { setSaving(false); }
       }}]);
  };

  const bulkExport = () => {
    const rows = [...selected].map(id => {
      const m = members.find(x => x.id === id);
      if (!m) return '';
      return `${m.name}, ${m.role || ''}, ${m.department || ''}, ${m.email || ''}, ${m.status || ''}`;
    }).filter(Boolean).join('\n');
    Share.share({ message: `Team Members Export\n\n${rows}`, title: 'Team Export' });
  };

  // ── Meeting CRUD ─────────────────────────────────────────────────────────────
  const openAddMeeting = () => {
    setEditMeeting(null);
    setMtf(blankMeeting());
    setMeetParticipants([]);
    setMeetModal(true);
  };

  const openEditMeeting = (m) => {
    setEditMeeting(m);
    setMtf({
      title: m.title || '', meeting_date: m.meeting_date || TODAY, time: m.time || '09:00',
      duration_minutes: String(m.duration_minutes || 60), type: m.type || 'one-time',
      location: m.location || '', agenda: m.agenda || '', notes: m.notes || '',
    });
    setMeetParticipants(m.participants || []);
    setMeetModal(true);
  };

  const saveMeeting = async () => {
    if (!mtf.title.trim()) { Alert.alert('Required', 'Meeting title is required.'); return; }
    setSaving(true);
    try {
      const payload = {
        company_id:       companyId,
        title:            mtf.title.trim(),
        meeting_date:     mtf.meeting_date,
        time:             mtf.time,
        duration_minutes: parseInt(mtf.duration_minutes) || 60,
        type:             mtf.type,
        location:         mtf.location.trim(),
        agenda:           mtf.agenda.trim(),
        notes:            mtf.notes.trim(),
        participants:     meetParticipants,
        status:           'scheduled',
      };
      editMeeting?.id
        ? await api(`/api/data/team_meetings/${editMeeting.id}`, 'PUT', payload)
        : await api('/api/data/team_meetings', 'POST', payload);
      setMeetModal(false);
      await fetchMeetings();
      Alert.alert('Scheduled', 'Meeting has been saved.');
    } catch { Alert.alert('Error', 'Could not save meeting.'); }
    finally { setSaving(false); }
  };

  const deleteMeeting = (m) => {
    Alert.alert('Cancel Meeting', `Cancel "${m.title}"?`,
      [{ text: 'No', style: 'cancel' },
       { text: 'Yes, Cancel', style: 'destructive', onPress: async () => {
          try { await api(`/api/data/team_meetings/${m.id}`, 'DELETE'); await fetchMeetings(); }
          catch { Alert.alert('Error', 'Could not cancel meeting.'); }
       }}]);
  };

  // ── Department CRUD ──────────────────────────────────────────────────────────
  const openAddDept = (prefill = {}) => {
    setEditDept(null);
    setDf({ ...blankDept(), ...prefill });
    setDeptModal(true);
  };

  const openEditDept = (d) => {
    setEditDept(d);
    setDf({ name: d.name || '', head: d.head || '', description: d.description || '',
      color: d.color || PALETTE[0], budget: d.budget ? String(d.budget) : '' });
    setDeptModal(true);
  };

  const saveDept = async () => {
    if (!df.name.trim()) { Alert.alert('Required', 'Department name is required.'); return; }
    setSaving(true);
    try {
      const payload = { company_id: companyId, name: df.name.trim(), head: df.head.trim(),
        description: df.description.trim(), color: df.color,
        budget: df.budget ? parseFloat(df.budget) : null };
      editDept?.id
        ? await api(`/api/data/team_departments/${editDept.id}`, 'PUT', payload)
        : await api('/api/data/team_departments', 'POST', payload);
      setDeptModal(false);
      await fetchDepartments();
    } catch { Alert.alert('Error', 'Could not save department.'); }
    finally { setSaving(false); }
  };

  const deleteDept = (d) => {
    Alert.alert('Delete Department', `Delete "${d.name}"? Members will not be deleted.`,
      [{ text: 'Cancel', style: 'cancel' },
       { text: 'Delete', style: 'destructive', onPress: async () => {
          try { await api(`/api/data/team_departments/${d.id}`, 'DELETE'); await fetchDepartments(); }
          catch { Alert.alert('Error', 'Could not delete department.'); }
       }}]);
  };

  // ── Computed values ──────────────────────────────────────────────────────────
  const deptCount = (name) => members.filter(m => m.department === name).length;
  const deptColor = (name) => {
    if (!name) return PALETTE[0];
    const d = departments.find(x => x.name === name);
    return d?.color || colorFor(name);
  };

  const filteredMembers = members
    .filter(m => {
      const q = searchQ.toLowerCase();
      const matchQ = !q || [m.name, m.role, m.email, m.department, m.manager, m.territory]
        .some(v => (v || '').toLowerCase().includes(q));
      const matchDept   = !filterDept     || m.department    === filterDept;
      const matchStatus = !filterStatus   || m.status        === filterStatus;
      const matchContr  = !filterContract || m.contract_type === filterContract;
      return matchQ && matchDept && matchStatus && matchContr;
    })
    .sort((a, b) => {
      let diff = 0;
      if (sortKey === 'name')        diff = (a.name || '').localeCompare(b.name || '');
      else if (sortKey === 'performance') diff = (b.performance_score || b.performance || 0) - (a.performance_score || a.performance || 0);
      else if (sortKey === 'joinDate') diff = (a.join_date || a.joinDate || '').localeCompare(b.join_date || b.joinDate || '');
      else if (sortKey === 'department') diff = (a.department || '').localeCompare(b.department || '');
      else if (sortKey === 'status') diff = (a.status || '').localeCompare(b.status || '');
      return sortDir === 'asc' ? diff : -diff;
    });

  const filteredMeetings = meetings.filter(m => {
    const q = meetSearch.toLowerCase();
    const matchQ = !q || (m.title || '').toLowerCase().includes(q) || (m.location || '').toLowerCase().includes(q);
    const matchType = !meetFilter || m.type === meetFilter;
    return matchQ && matchType;
  });

  const totalMembers   = members.length;
  const activeMembers  = members.filter(m => m.status === 'active').length;
  const remoteMembers  = members.filter(m => m.status === 'remote').length;
  const onLeave        = members.filter(m => m.status === 'on_leave').length;
  const onlineMembers  = members.filter(m => m.is_online || m.isOnline).length;
  const avgPerf        = totalMembers > 0
    ? Math.round(members.reduce((s, m) => s + (m.performance_score || m.performance || 0), 0) / totalMembers) : 0;
  const totalPayroll   = members.reduce((s, m) => s + (m.salary || 0), 0);

  // ── Tab: Overview ────────────────────────────────────────────────────────────
  const renderOverview = () => (
    <ScrollView showsVerticalScrollIndicator={false}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}>

      {/* Hero Banner */}
      <LinearGradient colors={['#0e4e96', '#1565C0']} style={St.hero}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <View>
            <Text style={St.heroTitle}>Team Overview</Text>
            <Text style={St.heroSub}>Manage your entire workforce</Text>
          </View>
          <TouchableOpacity onPress={() => navigation.navigate('TeamAnalyticsScreen', { members, departments, companyId })}
            style={{ backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 10, paddingHorizontal: 12, paddingVertical: 6, flexDirection: 'row', alignItems: 'center', gap: 4 }}>
            <Ionicons name="bar-chart-outline" size={16} color="#fff" />
            <Text style={{ color: '#fff', fontSize: 12, fontWeight: '700' }}>Analytics</Text>
          </TouchableOpacity>
        </View>
        <View style={St.heroGrid}>
          {[
            { label: 'Total',       value: totalMembers,  icon: 'people'          },
            { label: 'Active',      value: activeMembers, icon: 'checkmark-circle'},
            { label: 'Remote',      value: remoteMembers, icon: 'wifi'            },
            { label: 'On Leave',    value: onLeave,       icon: 'bed'             },
            { label: 'Online Now',  value: onlineMembers, icon: 'radio-button-on' },
            { label: 'Depts',       value: departments.length, icon: 'business'   },
          ].map(s => (
            <View key={s.label} style={St.heroStat}>
              <Ionicons name={s.icon} size={18} color="rgba(255,255,255,0.75)" />
              <Text style={St.heroStatNum}>{s.value}</Text>
              <Text style={St.heroStatLabel}>{s.label}</Text>
            </View>
          ))}
        </View>
        {avgPerf > 0 && (
          <View style={St.heroPerfRow}>
            <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>Avg Performance</Text>
            <View style={{ flex: 1, marginHorizontal: 12, height: 6, backgroundColor: 'rgba(255,255,255,0.25)', borderRadius: 3, overflow: 'hidden' }}>
              <View style={{ width: `${avgPerf}%`, height: '100%', backgroundColor: '#69F0AE', borderRadius: 3 }} />
            </View>
            <Text style={{ color: '#fff', fontSize: 13, fontWeight: '800' }}>{avgPerf}%</Text>
          </View>
        )}
      </LinearGradient>

      {/* Quick Actions */}
      <View style={[St.card, { paddingBottom: 14 }]}>
        <SectionHeader title="Quick Actions" />
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
          {[
            { label: 'Add Member',   icon: 'person-add',  colors: ['#388E3C','#4CAF50'], action: () => openAddMember()   },
            { label: 'Schedule',     icon: 'videocam',    colors: ['#1565C0','#1976D2'], action: openAddMeeting           },
            { label: 'Add Dept',     icon: 'layers',      colors: ['#E65100','#F57C00'], action: () => openAddDept()      },
            { label: 'Analytics',    icon: 'bar-chart',   colors: ['#6A1B9A','#7B1FA2'], action: () => navigation.navigate('TeamAnalyticsScreen', { members, departments, companyId }) },
            { label: 'Export',       icon: 'share-social',colors: ['#00695C','#00897B'],
              action: () => Share.share({ message: `Team Report\nTotal: ${totalMembers} | Active: ${activeMembers} | Avg Perf: ${avgPerf}%\nPayroll: ${fmtCurrency(totalPayroll)}/yr` }) },
            { label: 'Company',      icon: 'business',    colors: ['#0e4e96','#1565C0'], action: () => navigation.navigate('CompanyProfileScreen', { companyProfile }) },
            { label: 'My Profile',   icon: 'person',      colors: ['#00838F','#0097A7'], action: () => navigation.navigate('BusinessUserProfileScreen') },
            { label: 'Video Call',   icon: 'videocam',    colors: ['#F57C00','#E65100'], action: () => navigation.navigate('BusinessVideoConferenceScreen', { companyId }) },
          ].map(a => (
            <TouchableOpacity key={a.label} style={{ width: (width - 60) / 3 }} onPress={a.action}>
              <LinearGradient colors={a.colors} style={St.qaBtn}>
                <Ionicons name={a.icon} size={22} color="#fff" />
                <Text style={St.qaBtnText}>{a.label}</Text>
              </LinearGradient>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Status breakdown */}
      <View style={St.card}>
        <SectionHeader title="Workforce Status" />
        {STATUS_LIST.map(s => {
          const count = members.filter(m => m.status === s).length;
          const pct = totalMembers > 0 ? (count / totalMembers) * 100 : 0;
          const meta = statusMeta(s);
          return (
            <TouchableOpacity key={s} style={St.statusRow}
              onPress={() => { setFilterStatus(s); setActiveTab('members'); }}>
              <View style={[St.statusDot, { backgroundColor: meta.fg }]} />
              <Text style={St.statusLabel}>{meta.label}</Text>
              <View style={{ flex: 1, marginHorizontal: 12, height: 8, backgroundColor: '#ECEFF1', borderRadius: 4, overflow: 'hidden' }}>
                <View style={{ width: `${pct}%`, height: '100%', backgroundColor: meta.fg, borderRadius: 4 }} />
              </View>
              <Text style={{ fontSize: 13, fontWeight: '700', color: meta.fg, width: 36, textAlign: 'right' }}>{count}</Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Departments */}
      <View style={St.card}>
        <SectionHeader title="Departments" count={departments.length} onAdd={() => openAddDept()} />
        {departments.length === 0 ? (
          <EmptyState icon="business-outline" title="No departments yet"
            sub="Organise your team into departments" onAction={() => openAddDept()} actionLabel="Add Department" />
        ) : (
          departments.map((d, i) => {
            const count = deptCount(d.name);
            const pct = totalMembers > 0 ? (count / totalMembers) * 100 : 0;
            const color = d.color || PALETTE[i % PALETTE.length];
            return (
              <View key={d.id || i}>
                <TouchableOpacity style={St.deptRow}
                  onPress={() => { setFilterDept(d.name); setActiveTab('members'); }}>
                  <View style={[St.deptDot, { backgroundColor: color }]} />
                  <View style={{ flex: 1 }}>
                    <Text style={St.deptName}>{d.name}</Text>
                    {d.head ? <Text style={St.deptHead}>{d.head}</Text> : null}
                    <View style={{ height: 5, backgroundColor: '#ECEFF1', borderRadius: 3, marginTop: 4, overflow: 'hidden' }}>
                      <View style={{ width: `${pct}%`, height: '100%', backgroundColor: color, borderRadius: 3 }} />
                    </View>
                  </View>
                  <Text style={St.deptCount}>{count}</Text>
                  <View style={{ flexDirection: 'row', gap: 4, marginLeft: 8 }}>
                    <TouchableOpacity onPress={() => openEditDept(d)} style={St.iconBtn}>
                      <Ionicons name="create-outline" size={16} color="#0e4e96" />
                    </TouchableOpacity>
                    <TouchableOpacity onPress={() => deleteDept(d)} style={[St.iconBtn, { backgroundColor: '#FFEBEE' }]}>
                      <Ionicons name="trash-outline" size={16} color="#D32F2F" />
                    </TouchableOpacity>
                  </View>
                </TouchableOpacity>
                {i < departments.length - 1 && <Divider />}
              </View>
            );
          })
        )}
      </View>

      {/* Payroll summary */}
      {totalPayroll > 0 && (
        <View style={St.card}>
          <SectionHeader title="Payroll Summary" />
          <View style={{ flexDirection: 'row', gap: 10 }}>
            <View style={[St.kpiBox, { flex: 1 }]}>
              <Text style={St.kpiBoxLabel}>Annual Payroll</Text>
              <Text style={[St.kpiBoxValue, { color: '#0e4e96' }]}>{fmtCurrency(totalPayroll)}</Text>
            </View>
            <View style={[St.kpiBox, { flex: 1 }]}>
              <Text style={St.kpiBoxLabel}>Avg Salary</Text>
              <Text style={[St.kpiBoxValue, { color: '#0e4e96' }]}>
                {totalMembers > 0 ? fmtCurrency(Math.round(totalPayroll / totalMembers)) : '—'}
              </Text>
            </View>
          </View>
          {CONTRACT_TYPES.map(ct => {
            const cnt = members.filter(m => m.contract_type === ct).length;
            if (!cnt) return null;
            return (
              <View key={ct} style={[St.statusRow, { marginTop: 6 }]}>
                <Text style={[St.statusLabel, { width: 110 }]}>{ct}</Text>
                <View style={{ flex: 1, marginHorizontal: 10, height: 6, backgroundColor: '#ECEFF1', borderRadius: 3, overflow: 'hidden' }}>
                  <View style={{ width: `${(cnt / totalMembers) * 100}%`, height: '100%', backgroundColor: '#0e4e96', borderRadius: 3 }} />
                </View>
                <Text style={{ fontSize: 13, fontWeight: '700', color: '#0e4e96', width: 24, textAlign: 'right' }}>{cnt}</Text>
              </View>
            );
          })}
        </View>
      )}

      {/* Top performers */}
      {members.some(m => (m.performance_score || m.performance) > 0) && (
        <View style={St.card}>
          <SectionHeader title="Top Performers" onAdd={() => setActiveTab('members')} addLabel="All →" />
          {[...members]
            .filter(m => (m.performance_score || m.performance || 0) > 0)
            .sort((a, b) => (b.performance_score || b.performance || 0) - (a.performance_score || a.performance || 0))
            .slice(0, 5)
            .map((m, i) => (
              <TouchableOpacity key={m.id} style={St.topRow}
                onPress={() => { setViewMember(m); setDetailModal(true); setDetailTab('info'); }}>
                <Text style={[St.rankText, { color: i < 3 ? '#F57C00' : '#B0BEC5' }]}>#{i + 1}</Text>
                <Avatar name={m.name} size={36} color={deptColor(m.department)} />
                <View style={{ flex: 1, marginLeft: 10 }}>
                  <Text style={St.topName}>{m.name}</Text>
                  <Text style={St.topRole}>{m.role || m.department || '—'}</Text>
                  <PerfBar value={m.performance_score || m.performance || 0} height={5} />
                </View>
                <Text style={{ fontSize: 15, fontWeight: '800', color: perfColor(m.performance_score || m.performance || 0), marginLeft: 10 }}>
                  {m.performance_score || m.performance || 0}%
                </Text>
              </TouchableOpacity>
            ))}
        </View>
      )}

      {/* Skills cloud */}
      {members.some(m => (m.skills || []).length > 0 || typeof m.skills === 'string') && (
        <View style={[St.card, { marginBottom: 10 }]}>
          <SectionHeader title="Skills in Team" />
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 7 }}>
            {[...new Set(
              members.flatMap(m => Array.isArray(m.skills) ? m.skills : (m.skills ? String(m.skills).split(',').map(s => s.trim()) : []))
                .filter(Boolean)
            )].slice(0, 30).map(skill => (
              <TouchableOpacity key={skill}
                onPress={() => { setSearchQ(skill); setActiveTab('members'); }}
                style={[St.chip, { backgroundColor: '#E3F2FD', borderColor: '#BBDEFB' }]}>
                <Text style={[St.chipText, { color: '#0e4e96' }]}>{skill}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      )}

      {/* Upcoming meetings snippet */}
      <View style={[St.card, { marginBottom: 24 }]}>
        <SectionHeader title="Upcoming Meetings" count={meetings.length} onAdd={openAddMeeting} addLabel="Schedule" />
        {meetings.length === 0 ? (
          <EmptyState icon="calendar-outline" title="No upcoming meetings"
            onAction={openAddMeeting} actionLabel="Schedule Meeting" />
        ) : (
          meetings.slice(0, 4).map((m, i) => (
            <View key={m.id || i}>
              <View style={St.meetSnip}>
                <View style={[St.meetDot, { backgroundColor: m.type === 'recurring' ? '#F57C00' : '#0e4e96' }]} />
                <View style={{ flex: 1 }}>
                  <Text style={St.meetSnipTitle} numberOfLines={1}>{m.title}</Text>
                  <Text style={St.meetSnipMeta}>{m.meeting_date} · {m.time} · {m.duration_minutes || 60}min</Text>
                </View>
                <TouchableOpacity style={St.joinPill}
                  onPress={() => navigation.navigate('BusinessVideoConferenceScreen', { meetingId: m.id, meetingTitle: m.title, companyId })}>
                  <Text style={St.joinPillText}>Join</Text>
                </TouchableOpacity>
              </View>
              {i < Math.min(meetings.length, 4) - 1 && <Divider />}
            </View>
          ))
        )}
        {meetings.length > 4 && (
          <TouchableOpacity onPress={() => setActiveTab('meetings')} style={{ alignItems: 'center', paddingTop: 10 }}>
            <Text style={{ color: '#0e4e96', fontSize: 13, fontWeight: '600' }}>View all {meetings.length} meetings →</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Team Health Score */}
      {(() => {
        const activeRatio   = totalMembers > 0 ? activeMembers / totalMembers : 0;
        const perfRatio     = avgPerf / 100;
        const deptRatio     = Math.min(departments.length / 5, 1);
        const meetRatio     = Math.min(meetings.length / 3, 1);
        const healthScore   = Math.round((activeRatio * 30 + perfRatio * 40 + deptRatio * 15 + meetRatio * 15));
        const hColor        = healthScore >= 80 ? '#388E3C' : healthScore >= 60 ? '#F57C00' : '#D32F2F';
        const hLabel        = healthScore >= 80 ? 'Excellent' : healthScore >= 60 ? 'Good' : 'Needs Attention';
        return (
          <View style={[St.card, { marginBottom: 10 }]}>
            <SectionHeader title="Team Health Score" />
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
              <View style={{ width: 72, height: 72, borderRadius: 36, borderWidth: 5, borderColor: hColor,
                alignItems: 'center', justifyContent: 'center' }}>
                <Text style={{ fontSize: 22, fontWeight: '900', color: hColor }}>{healthScore}</Text>
              </View>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 15, fontWeight: '800', color: hColor }}>{hLabel}</Text>
                <Text style={{ fontSize: 12, color: '#78909C', marginTop: 3 }}>Based on activity, performance & coverage</Text>
                {[
                  { label: 'Active Ratio',    val: Math.round(activeRatio * 100), w: 30 },
                  { label: 'Avg Performance', val: avgPerf,                       w: 40 },
                  { label: 'Dept Coverage',   val: Math.round(deptRatio * 100),   w: 15 },
                  { label: 'Meeting Cadence', val: Math.round(meetRatio * 100),   w: 15 },
                ].map(r => (
                  <View key={r.label} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 5 }}>
                    <Text style={{ fontSize: 10, color: '#90A4AE', width: 100 }}>{r.label}</Text>
                    <View style={{ flex: 1, height: 4, backgroundColor: '#ECEFF1', borderRadius: 2, overflow: 'hidden' }}>
                      <View style={{ width: `${r.val}%`, height: '100%', backgroundColor: perfColor(r.val), borderRadius: 2 }} />
                    </View>
                    <Text style={{ fontSize: 10, fontWeight: '700', color: perfColor(r.val), width: 28, textAlign: 'right' }}>{r.val}%</Text>
                  </View>
                ))}
              </View>
            </View>
          </View>
        );
      })()}

      {/* New Hires This Month */}
      {(() => {
        const thisMonth = new Date().toISOString().slice(0, 7);
        const newHires  = members.filter(m => (m.join_date || m.joinDate || '').startsWith(thisMonth));
        if (!newHires.length) return null;
        return (
          <View style={[St.card, { marginBottom: 10 }]}>
            <SectionHeader title={`New Hires This Month (${newHires.length})`} />
            {newHires.map(m => (
              <TouchableOpacity key={m.id} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8, gap: 12,
                borderBottomWidth: 1, borderBottomColor: '#F5F7FA' }}
                onPress={() => { setViewMember(m); setDetailModal(true); setDetailTab('info'); }}>
                <Avatar name={m.name} size={40} color={deptColor(m.department)} />
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 14, fontWeight: '700', color: '#263238' }}>{m.name}</Text>
                  <Text style={{ fontSize: 12, color: '#78909C' }}>{m.role || '—'} · {m.department || '—'}</Text>
                </View>
                <View style={{ backgroundColor: '#E8F5E9', borderRadius: 10, paddingHorizontal: 8, paddingVertical: 3 }}>
                  <Text style={{ fontSize: 10, color: '#2E7D32', fontWeight: '700' }}>NEW</Text>
                </View>
              </TouchableOpacity>
            ))}
          </View>
        );
      })()}

      {/* Team Capacity Overview */}
      {totalMembers > 0 && (
        <View style={[St.card, { marginBottom: 24 }]}>
          <SectionHeader title="Team Capacity" />
          <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
            {[
              { label: 'In Office', value: members.filter(m => m.status === 'active' && !m.is_remote).length, color: '#388E3C', icon: 'business-outline' },
              { label: 'Remote',    value: remoteMembers,  color: '#1976D2', icon: 'wifi' },
              { label: 'On Leave',  value: onLeave,        color: '#F57C00', icon: 'bed' },
              { label: 'Inactive',  value: members.filter(m => m.status === 'inactive').length, color: '#D32F2F', icon: 'close-circle-outline' },
            ].map(c => (
              <View key={c.label} style={{ width: (width - 68) / 2, backgroundColor: c.color + '12',
                borderRadius: 12, padding: 12, borderWidth: 1, borderColor: c.color + '30' }}>
                <Ionicons name={c.icon} size={18} color={c.color} />
                <Text style={{ fontSize: 22, fontWeight: '900', color: c.color, marginTop: 4 }}>{c.value}</Text>
                <Text style={{ fontSize: 11, color: c.color, fontWeight: '600' }}>{c.label}</Text>
                <Text style={{ fontSize: 10, color: '#90A4AE', marginTop: 2 }}>
                  {totalMembers > 0 ? Math.round((c.value / totalMembers) * 100) : 0}% of team
                </Text>
              </View>
            ))}
          </View>
        </View>
      )}
    </ScrollView>
  );

  // ── Tab: Members ─────────────────────────────────────────────────────────────
  const renderMembers = () => (
    <View style={{ flex: 1 }}>
      {/* Search + filter row */}
      <View style={St.searchBar}>
        <View style={St.searchBox}>
          <Ionicons name="search" size={17} color="#90A4AE" />
          <TextInput style={St.searchInput} value={searchQ} onChangeText={setSearchQ}
            placeholder="Name, role, email, manager…" placeholderTextColor="#B0BEC5" autoCorrect={false} />
          {searchQ ? <TouchableOpacity onPress={() => setSearchQ('')}><Ionicons name="close-circle" size={17} color="#90A4AE" /></TouchableOpacity> : null}
        </View>
        <TouchableOpacity onPress={() => setShowFilters(v => !v)}
          style={[St.iconBtn, (filterDept || filterStatus || filterContract) && { backgroundColor: '#E3F2FD' }]}>
          <Ionicons name="options" size={20} color={filterDept || filterStatus || filterContract ? '#0e4e96' : '#607D8B'} />
        </TouchableOpacity>
        {!bulkMode ? (
          <TouchableOpacity style={St.addBtn} onPress={() => openAddMember()}>
            <Ionicons name="person-add" size={19} color="#fff" />
          </TouchableOpacity>
        ) : (
          <TouchableOpacity style={[St.addBtn, { backgroundColor: selected.size > 0 ? '#F57C00' : '#B0BEC5' }]}
            onPress={() => selected.size > 0 && setBulkModal(true)}>
            <Text style={{ color: '#fff', fontSize: 12, fontWeight: '800' }}>{selected.size}</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Sort + bulk row */}
      <View style={St.sortRow}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 6, paddingHorizontal: 14 }}>
          {SORT_OPTIONS.map(s => (
            <TouchableOpacity key={s.key} onPress={() => {
              if (sortKey === s.key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
              else { setSortKey(s.key); setSortDir('asc'); }
            }} style={[St.sortBtn, sortKey === s.key && St.sortBtnActive]}>
              <Text style={[St.sortBtnText, sortKey === s.key && St.sortBtnTextActive]}>
                {s.label} {sortKey === s.key ? (sortDir === 'asc' ? '↑' : '↓') : ''}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
        <TouchableOpacity onPress={() => { setBulkMode(v => !v); setSelected(new Set()); }}
          style={[St.iconBtn, { marginRight: 14 }, bulkMode && { backgroundColor: '#FFF3E0' }]}>
          <Ionicons name={bulkMode ? 'checkmark-circle' : 'checkmark-circle-outline'} size={20} color={bulkMode ? '#F57C00' : '#607D8B'} />
        </TouchableOpacity>
      </View>

      {/* Filter panel */}
      {showFilters && (
        <View style={St.filterPanel}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 7, paddingHorizontal: 14, paddingVertical: 6 }}>
            <Chip label="All Status" active={!filterStatus} onPress={() => setFilterStatus('')} />
            {STATUS_LIST.map(s => <Chip key={s} label={statusMeta(s).label} active={filterStatus === s} onPress={() => setFilterStatus(filterStatus === s ? '' : s)} />)}
          </ScrollView>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 7, paddingHorizontal: 14, paddingVertical: 4 }}>
            <Chip label="All Depts" active={!filterDept} onPress={() => setFilterDept('')} />
            {departments.map(d => <Chip key={d.id} label={d.name} active={filterDept === d.name} color={d.color} onPress={() => setFilterDept(filterDept === d.name ? '' : d.name)} />)}
          </ScrollView>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 7, paddingHorizontal: 14, paddingVertical: 6 }}>
            <Chip label="All Contracts" active={!filterContract} onPress={() => setFilterContract('')} />
            {CONTRACT_TYPES.map(c => <Chip key={c} label={c} active={filterContract === c} onPress={() => setFilterContract(filterContract === c ? '' : c)} />)}
          </ScrollView>
          {bulkMode && selected.size < filteredMembers.length && (
            <TouchableOpacity onPress={selectAll} style={{ paddingHorizontal: 14, paddingBottom: 6 }}>
              <Text style={{ color: '#0e4e96', fontSize: 13, fontWeight: '600' }}>Select All ({filteredMembers.length})</Text>
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* Results count */}
      <View style={{ paddingHorizontal: 16, paddingVertical: 8, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text style={{ fontSize: 12, color: '#90A4AE' }}>
          {filteredMembers.length} of {totalMembers} members
          {(filterDept || filterStatus || filterContract || searchQ) ? ' (filtered)' : ''}
        </Text>
        {(filterDept || filterStatus || filterContract || searchQ) && (
          <TouchableOpacity onPress={() => { setSearchQ(''); setFilterDept(''); setFilterStatus(''); setFilterContract(''); }}>
            <Text style={{ fontSize: 12, color: '#D32F2F', fontWeight: '600' }}>Clear filters</Text>
          </TouchableOpacity>
        )}
      </View>

      <FlatList
        data={filteredMembers}
        keyExtractor={m => String(m.id)}
        contentContainerStyle={{ paddingHorizontal: 14, paddingBottom: 30 }}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        ListEmptyComponent={
          <EmptyState icon="people-outline"
            title={searchQ || filterDept || filterStatus || filterContract ? 'No matches' : 'No team members yet'}
            sub={searchQ || filterDept || filterStatus || filterContract ? 'Try adjusting your filters' : 'Add your first team member'}
            onAction={!searchQ && !filterDept && !filterStatus && !filterContract ? () => openAddMember() : null}
            actionLabel="Add Member" />
        }
        renderItem={({ item: m }) => {
          const perf = m.performance_score || m.performance || 0;
          const sel = selected.has(m.id);
          return (
            <TouchableOpacity style={[St.memberCard, sel && { borderColor: '#0e4e96', borderWidth: 2 }]}
              onPress={() => {
                if (bulkMode) { toggleSelect(m.id); }
                else { setViewMember(m); setDetailModal(true); setDetailTab('info'); }
              }}
              onLongPress={() => { setBulkMode(true); toggleSelect(m.id); }}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                {bulkMode && (
                  <View style={{ marginRight: 10 }}>
                    <Ionicons name={sel ? 'checkbox' : 'square-outline'} size={22} color={sel ? '#0e4e96' : '#B0BEC5'} />
                  </View>
                )}
                <Avatar name={m.name} size={48} color={deptColor(m.department)} online={m.is_online || m.isOnline} />
                <View style={{ flex: 1, marginLeft: 12 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Text style={St.mName}>{m.name}</Text>
                    <Badge status={m.status} />
                    {m.contract_type && m.contract_type !== 'Permanent' && (
                      <View style={{ backgroundColor: '#F3E5F5', borderRadius: 8, paddingHorizontal: 6, paddingVertical: 2 }}>
                        <Text style={{ fontSize: 10, color: '#6A1B9A', fontWeight: '700' }}>{m.contract_type}</Text>
                      </View>
                    )}
                  </View>
                  <Text style={St.mRole}>{m.role || '—'}</Text>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    {m.department && <Text style={St.mDept}>{m.department}</Text>}
                    {m.manager && <Text style={[St.mDept, { color: '#90A4AE' }]}>· {m.manager}</Text>}
                  </View>
                </View>
                <View style={{ alignItems: 'flex-end', gap: 4 }}>
                  {perf > 0 && <Text style={[St.perfNum, { color: perfColor(perf) }]}>{perf}%</Text>}
                  {m.salary > 0 && <Text style={{ fontSize: 11, color: '#90A4AE' }}>{fmtCurrency(m.salary)}</Text>}
                  <View style={{ flexDirection: 'row', gap: 4, alignItems: 'center' }}>
                    <TouchableOpacity
                      onPress={(e) => { e.stopPropagation?.(); navigation.navigate('BusinessUserProfileScreen', { prefillUserId: m.user_id || m.id, prefillName: m.name }); }}
                      style={{ padding: 2 }}>
                      <Ionicons name="person-circle-outline" size={17} color="#0e4e96" />
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={(e) => { e.stopPropagation?.(); setExpandedCard(expandedCard === m.id ? null : m.id); }}
                      style={{ padding: 2 }}>
                      <Ionicons name={expandedCard === m.id ? 'chevron-up' : 'chevron-down'} size={14} color="#90A4AE" />
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
              {perf > 0 && <View style={{ marginTop: 10 }}><PerfBar value={perf} /></View>}
              {/* Expanded quick actions */}
              {expandedCard === m.id && !bulkMode && (
                <View style={{ flexDirection: 'row', gap: 6, marginTop: 10, paddingTop: 10,
                  borderTopWidth: 1, borderTopColor: '#F5F7FA' }}>
                  {m.email && (
                    <TouchableOpacity style={[St.qaBtnSmall, { backgroundColor: '#E3F2FD' }]}
                      onPress={() => Linking.openURL(`mailto:${m.email}`)}>
                      <Ionicons name="mail-outline" size={14} color="#0e4e96" />
                      <Text style={[St.qaBtnSmallText, { color: '#0e4e96' }]}>Email</Text>
                    </TouchableOpacity>
                  )}
                  {m.phone && (
                    <TouchableOpacity style={[St.qaBtnSmall, { backgroundColor: '#E8F5E9' }]}
                      onPress={() => Linking.openURL(`tel:${m.phone}`)}>
                      <Ionicons name="call-outline" size={14} color="#388E3C" />
                      <Text style={[St.qaBtnSmallText, { color: '#388E3C' }]}>Call</Text>
                    </TouchableOpacity>
                  )}
                  <TouchableOpacity style={[St.qaBtnSmall, { backgroundColor: '#FFF3E0' }]}
                    onPress={() => navigation.navigate('BusinessVideoConferenceScreen', { participantName: m.name, companyId })}>
                    <Ionicons name="videocam-outline" size={14} color="#F57C00" />
                    <Text style={[St.qaBtnSmallText, { color: '#F57C00' }]}>Call</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={[St.qaBtnSmall, { backgroundColor: '#F3E5F5' }]}
                    onPress={() => openPerfUpdate(m)}>
                    <Ionicons name="trending-up-outline" size={14} color="#7B1FA2" />
                    <Text style={[St.qaBtnSmallText, { color: '#7B1FA2' }]}>Perf</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={[St.qaBtnSmall, { backgroundColor: '#FBE9E7' }]}
                    onPress={() => openNoteAdd(m)}>
                    <Ionicons name="document-text-outline" size={14} color="#D84315" />
                    <Text style={[St.qaBtnSmallText, { color: '#D84315' }]}>Note</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={[St.qaBtnSmall, { backgroundColor: '#E8F5E9' }]}
                    onPress={() => { setDetailModal(false); navigation.navigate('BusinessUserProfileScreen', { prefillUserId: m.user_id || m.id, prefillName: m.name }); }}>
                    <Ionicons name="person-circle-outline" size={14} color="#2E7D32" />
                    <Text style={[St.qaBtnSmallText, { color: '#2E7D32' }]}>Profile</Text>
                  </TouchableOpacity>
                </View>
              )}
            </TouchableOpacity>
          );
        }}
      />
    </View>
  );

  // ── Tab: Meetings ────────────────────────────────────────────────────────────
  const renderMeetings = () => (
    <View style={{ flex: 1 }}>
      <View style={St.searchBar}>
        <View style={St.searchBox}>
          <Ionicons name="search" size={17} color="#90A4AE" />
          <TextInput style={St.searchInput} value={meetSearch} onChangeText={setMeetSearch}
            placeholder="Search meetings…" placeholderTextColor="#B0BEC5" />
          {meetSearch ? <TouchableOpacity onPress={() => setMeetSearch('')}><Ionicons name="close-circle" size={17} color="#90A4AE" /></TouchableOpacity> : null}
        </View>
        <TouchableOpacity style={St.addBtn} onPress={openAddMeeting}>
          <Ionicons name="add" size={22} color="#fff" />
        </TouchableOpacity>
      </View>

      {/* Type filter */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ gap: 7, paddingHorizontal: 14, paddingVertical: 8 }}>
        <Chip label="All Types" active={!meetFilter} onPress={() => setMeetFilter('')} />
        {MEETING_TYPES.map(t => <Chip key={t} label={t.charAt(0).toUpperCase() + t.slice(1)} active={meetFilter === t} onPress={() => setMeetFilter(meetFilter === t ? '' : t)} />)}
      </ScrollView>

      <FlatList
        data={filteredMeetings}
        keyExtractor={m => String(m.id)}
        contentContainerStyle={{ paddingHorizontal: 14, paddingBottom: 30 }}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        ListEmptyComponent={
          <EmptyState icon="calendar-outline" title="No upcoming meetings"
            sub="Schedule a meeting to see it here" onAction={openAddMeeting} actionLabel="Schedule Meeting" />
        }
        renderItem={({ item: m }) => {
          const isToday = m.meeting_date === TODAY;
          const paxCount = (m.participants || []).length;
          return (
            <View style={[St.meetCard, isToday && { borderLeftWidth: 4, borderLeftColor: '#0e4e96' }]}>
              {isToday && (
                <View style={{ backgroundColor: '#E3F2FD', borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3, alignSelf: 'flex-start', marginBottom: 8 }}>
                  <Text style={{ fontSize: 10, color: '#0e4e96', fontWeight: '800', letterSpacing: 0.5 }}>TODAY</Text>
                </View>
              )}
              <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
                <View style={[St.meetIconWrap, { backgroundColor: m.type === 'recurring' ? '#FFF3E0' : '#E3F2FD' }]}>
                  <Ionicons name={m.type === 'recurring' ? 'repeat' : 'videocam'} size={20}
                    color={m.type === 'recurring' ? '#F57C00' : '#0e4e96'} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={St.meetTitle}>{m.title}</Text>
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 4 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                      <Ionicons name="calendar-outline" size={12} color="#78909C" />
                      <Text style={St.meetMeta}>{m.meeting_date}</Text>
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                      <Ionicons name="time-outline" size={12} color="#78909C" />
                      <Text style={St.meetMeta}>{m.time} · {m.duration_minutes || 60}min</Text>
                    </View>
                    {m.location && (
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                        <Ionicons name="location-outline" size={12} color="#78909C" />
                        <Text style={St.meetMeta}>{m.location}</Text>
                      </View>
                    )}
                    {paxCount > 0 && (
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                        <Ionicons name="people-outline" size={12} color="#78909C" />
                        <Text style={St.meetMeta}>{paxCount} participants</Text>
                      </View>
                    )}
                  </View>
                  {m.agenda ? <Text style={St.meetAgenda} numberOfLines={2}>{m.agenda}</Text> : null}
                  {m.notes ? <Text style={[St.meetAgenda, { color: '#90A4AE' }]} numberOfLines={1}>{m.notes}</Text> : null}
                </View>
              </View>
              {/* Participant avatars */}
              {m.participants && m.participants.length > 0 && (() => {
                const paxMembers = members.filter(mem => m.participants.includes(mem.id));
                return paxMembers.length > 0 ? (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 8 }}>
                    {paxMembers.slice(0, 6).map(pm => (
                      <Avatar key={pm.id} name={pm.name} size={24} color={deptColor(pm.department)} />
                    ))}
                    {paxMembers.length > 6 && (
                      <Text style={{ fontSize: 11, color: '#90A4AE', marginLeft: 4 }}>+{paxMembers.length - 6}</Text>
                    )}
                    <Text style={{ fontSize: 11, color: '#78909C', marginLeft: 4 }}>{paxMembers.length} invited</Text>
                  </View>
                ) : null;
              })()}
              <View style={[St.meetActions, { marginTop: 12 }]}>
                <TouchableOpacity style={St.joinBtn}
                  onPress={() => navigation.navigate('BusinessVideoConferenceScreen', { meetingId: m.id, meetingTitle: m.title, companyId })}>
                  <Ionicons name="videocam" size={14} color="#fff" />
                  <Text style={St.joinBtnText}>Join Meeting</Text>
                </TouchableOpacity>
                {m.location && m.location.startsWith('http') && (
                  <TouchableOpacity style={[St.iconBtn, { backgroundColor: '#E8F5E9' }]}
                    onPress={() => Linking.openURL(m.location).catch(() => Alert.alert('Error', 'Cannot open link'))}>
                    <Ionicons name="open-outline" size={16} color="#388E3C" />
                  </TouchableOpacity>
                )}
                <TouchableOpacity style={St.iconBtn} onPress={() => openEditMeeting(m)}>
                  <Ionicons name="create-outline" size={16} color="#0e4e96" />
                </TouchableOpacity>
                <TouchableOpacity style={[St.iconBtn, { backgroundColor: '#FFEBEE' }]} onPress={() => deleteMeeting(m)}>
                  <Ionicons name="trash-outline" size={16} color="#D32F2F" />
                </TouchableOpacity>
                <TouchableOpacity style={St.iconBtn}
                  onPress={() => Share.share({ message: `${m.title}\n${m.meeting_date} at ${m.time}\n${m.location || 'No location'}` })}>
                  <Ionicons name="share-outline" size={16} color="#607D8B" />
                </TouchableOpacity>
              </View>
            </View>
          );
        }}
      />
    </View>
  );

  // ── Tab: Departments ─────────────────────────────────────────────────────────
  const renderDepartments = () => (
    <FlatList
      data={departments}
      keyExtractor={d => String(d.id)}
      contentContainerStyle={{ padding: 14, paddingBottom: 30 }}
      showsVerticalScrollIndicator={false}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      ListHeaderComponent={
        <TouchableOpacity style={St.addDeptBtnRow} onPress={() => openAddDept()}>
          <Ionicons name="add-circle" size={20} color="#0e4e96" />
          <Text style={{ color: '#0e4e96', fontSize: 14, fontWeight: '700' }}>Add Department</Text>
        </TouchableOpacity>
      }
      ListEmptyComponent={
        <EmptyState icon="business-outline" title="No departments" sub="Group your team into departments"
          onAction={() => openAddDept()} actionLabel="Add Department" />
      }
      renderItem={({ item: d, index: i }) => {
        const color  = d.color || PALETTE[i % PALETTE.length];
        const count  = deptCount(d.name);
        const dMembers = members.filter(m => m.department === d.name);
        const avgP   = dMembers.length > 0
          ? Math.round(dMembers.reduce((s, m) => s + (m.performance_score || m.performance || 0), 0) / dMembers.length) : 0;
        const totalSalary = dMembers.reduce((s, m) => s + (m.salary || 0), 0);
        return (
          <View style={[St.deptCard, { borderLeftColor: color }]}>
            <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
              <LinearGradient colors={[color, color + 'AA']} style={St.deptCardIcon}>
                <Ionicons name="business" size={20} color="#fff" />
              </LinearGradient>
              <View style={{ flex: 1, marginLeft: 12 }}>
                <Text style={St.deptCardName}>{d.name}</Text>
                {d.head ? (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5, marginTop: 2 }}>
                    <Ionicons name="person-outline" size={12} color="#78909C" />
                    <Text style={{ fontSize: 12, color: '#78909C' }}>{d.head}</Text>
                  </View>
                ) : null}
              </View>
              <View style={{ flexDirection: 'row', gap: 6 }}>
                <TouchableOpacity onPress={() => openEditDept(d)} style={St.iconBtn}>
                  <Ionicons name="create-outline" size={16} color="#0e4e96" />
                </TouchableOpacity>
                <TouchableOpacity onPress={() => deleteDept(d)} style={[St.iconBtn, { backgroundColor: '#FFEBEE' }]}>
                  <Ionicons name="trash-outline" size={16} color="#D32F2F" />
                </TouchableOpacity>
              </View>
            </View>

            {d.description ? <Text style={{ fontSize: 13, color: '#78909C', marginTop: 10, lineHeight: 18 }}>{d.description}</Text> : null}

            <View style={{ flexDirection: 'row', gap: 10, marginTop: 14 }}>
              {[
                { label: 'Members', value: String(count) },
                { label: 'Avg Perf', value: avgP > 0 ? `${avgP}%` : '—' },
                { label: 'Payroll', value: totalSalary > 0 ? fmtCurrency(totalSalary) : '—' },
                { label: 'Budget', value: d.budget ? fmtCurrency(d.budget) : '—' },
              ].map(k => (
                <View key={k.label} style={[St.kpiBox, { flex: 1, paddingVertical: 8 }]}>
                  <Text style={St.kpiBoxLabel}>{k.label}</Text>
                  <Text style={[St.kpiBoxValue, { fontSize: 14, color }]}>{k.value}</Text>
                </View>
              ))}
            </View>

            {avgP > 0 && (
              <View style={{ marginTop: 10 }}>
                <PerfBar value={avgP} height={8} showLabel />
              </View>
            )}

            {/* Budget utilization */}
            {d.budget > 0 && totalSalary > 0 && (
              <View style={{ marginTop: 10 }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                  <Text style={{ fontSize: 11, color: '#90A4AE', fontWeight: '600' }}>Budget Utilisation</Text>
                  <Text style={{ fontSize: 11, fontWeight: '700',
                    color: totalSalary > d.budget ? '#D32F2F' : '#388E3C' }}>
                    {Math.round((totalSalary / d.budget) * 100)}%
                    {totalSalary > d.budget ? ' ⚠ Over' : ''}
                  </Text>
                </View>
                <View style={{ height: 6, backgroundColor: '#ECEFF1', borderRadius: 3, overflow: 'hidden' }}>
                  <View style={{
                    width: `${Math.min((totalSalary / d.budget) * 100, 100)}%`,
                    height: '100%',
                    backgroundColor: totalSalary > d.budget ? '#D32F2F' : color,
                    borderRadius: 3,
                  }} />
                </View>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 3 }}>
                  <Text style={{ fontSize: 10, color: '#90A4AE' }}>Used: {fmtCurrency(totalSalary)}</Text>
                  <Text style={{ fontSize: 10, color: '#90A4AE' }}>Budget: {fmtCurrency(d.budget)}</Text>
                </View>
              </View>
            )}

            {/* Skills heatmap for dept */}
            {dMembers.length > 0 && (() => {
              const allSkills = [...new Set(
                dMembers.flatMap(m => Array.isArray(m.skills) ? m.skills : (m.skills ? String(m.skills).split(',').map(s => s.trim()) : []))
                  .filter(Boolean)
              )].slice(0, 8);
              return allSkills.length > 0 ? (
                <View style={{ marginTop: 10 }}>
                  <Text style={{ fontSize: 11, color: '#90A4AE', fontWeight: '600', marginBottom: 6 }}>TEAM SKILLS</Text>
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 5 }}>
                    {allSkills.map(s => (
                      <View key={s} style={{ backgroundColor: color + '20', borderRadius: 8,
                        paddingHorizontal: 8, paddingVertical: 3, borderWidth: 1, borderColor: color + '40' }}>
                        <Text style={{ fontSize: 10, color, fontWeight: '600' }}>{s}</Text>
                      </View>
                    ))}
                  </View>
                </View>
              ) : null;
            })()}

            {/* Mini avatar strip */}
            {dMembers.length > 0 && (
              <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 12, gap: 4 }}>
                {dMembers.slice(0, 6).map(m => <Avatar key={m.id} name={m.name} size={26} color={color} />)}
                {dMembers.length > 6 && <Text style={{ fontSize: 11, color: '#90A4AE', marginLeft: 4 }}>+{dMembers.length - 6} more</Text>}
              </View>
            )}

            <TouchableOpacity onPress={() => { setFilterDept(d.name); setActiveTab('members'); }}
              style={{ marginTop: 12, flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: 4 }}>
              <Text style={{ fontSize: 13, color: color, fontWeight: '700' }}>View Members</Text>
              <Ionicons name="arrow-forward" size={14} color={color} />
            </TouchableOpacity>
          </View>
        );
      }}
    />
  );

  // ── Member Detail Modal ──────────────────────────────────────────────────────
  const renderDetailModal = () => {
    const m = viewMember;
    if (!m) return null;
    const perf   = m.performance_score || m.performance || 0;
    const skills = Array.isArray(m.skills) ? m.skills : (m.skills ? String(m.skills).split(',').map(s => s.trim()).filter(Boolean) : []);
    const certs  = Array.isArray(m.certifications) ? m.certifications : (m.certifications ? String(m.certifications).split(',').map(s => s.trim()).filter(Boolean) : []);
    const langs  = Array.isArray(m.languages) ? m.languages : (m.languages ? String(m.languages).split(',').map(s => s.trim()).filter(Boolean) : []);
    const color  = deptColor(m.department);
    const DTABS  = ['info','performance','skills','contact'];

    return (
      <Modal visible={detailModal} animationType="slide" presentationStyle="pageSheet">
        <SafeAreaView style={{ flex: 1, backgroundColor: '#F5F7FA' }}>
          <View style={St.modalHeader}>
            <TouchableOpacity onPress={() => setDetailModal(false)}><Text style={St.modalCancel}>Close</Text></TouchableOpacity>
            <Text style={St.modalTitle} numberOfLines={1}>{m.name}</Text>
            <TouchableOpacity onPress={() => openEditMember(m)}><Text style={St.modalSave}>Edit</Text></TouchableOpacity>
          </View>

          {/* Hero */}
          <LinearGradient colors={[color, color + 'BB']} style={St.detailHero}>
            <Avatar name={m.name} size={74} color="rgba(255,255,255,0.25)" online={m.is_online || m.isOnline} />
            <Text style={St.detailName}>{m.name}</Text>
            <Text style={St.detailRole}>{m.role || '—'}</Text>
            <View style={{ flexDirection: 'row', gap: 8, marginTop: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Badge status={m.status} />
              {m.contract_type && m.contract_type !== 'Permanent' && (
                <View style={{ backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: 10, paddingHorizontal: 8, paddingVertical: 2 }}>
                  <Text style={{ fontSize: 11, color: '#fff', fontWeight: '600' }}>{m.contract_type}</Text>
                </View>
              )}
            </View>
          </LinearGradient>

          {/* Detail tabs */}
          <View style={{ flexDirection: 'row', backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#ECEFF1' }}>
            {DTABS.map(t => (
              <TouchableOpacity key={t} style={[{ flex: 1, paddingVertical: 10, alignItems: 'center' },
                detailTab === t && { borderBottomWidth: 2, borderBottomColor: color }]}
                onPress={() => setDetailTab(t)}>
                <Text style={{ fontSize: 12, fontWeight: '700', color: detailTab === t ? color : '#90A4AE', textTransform: 'capitalize' }}>{t}</Text>
              </TouchableOpacity>
            ))}
          </View>

          <ScrollView style={{ flex: 1 }}>
            {detailTab === 'info' && (
              <View>
                <View style={St.detailSection}>
                  <Text style={St.detailSectionTitle}>Employment</Text>
                  <InfoRow icon="briefcase-outline"  label="Role"        value={m.role} />
                  <InfoRow icon="business-outline"   label="Department"  value={m.department} />
                  <InfoRow icon="person-outline"     label="Manager"     value={m.manager} />
                  <InfoRow icon="location-outline"   label="Territory"   value={m.territory} />
                  <InfoRow icon="calendar-outline"   label="Joined"      value={fmtDate(m.join_date || m.joinDate)} />
                  <InfoRow icon="document-text-outline" label="Contract" value={m.contract_type} />
                  <InfoRow icon="cash-outline"       label="Salary"       value={m.salary ? fmtCurrency(m.salary) : null} />
                  <InfoRow icon="flag-outline"       label="Target"       value={m.target ? fmtCurrency(m.target) : null} />
                  <InfoRow icon="trending-up-outline" label="Revenue"     value={m.revenue ? fmtCurrency(m.revenue) : null} />
                  <InfoRow icon="briefcase-outline"  label="Experience"   value={m.experience_years ? `${m.experience_years} years` : null} />
                  <InfoRow icon="office-building"    label="Office"       value={m.office_location} />
                  <InfoRow icon="account-group"      label="Team"         value={m.team} />
                  <InfoRow icon="identifier"         label="Employee ID"  value={m.employee_id} />
                </View>
                {m.notes ? (
                  <View style={St.detailSection}>
                    <Text style={St.detailSectionTitle}>Notes</Text>
                    <Text style={{ fontSize: 14, color: '#546E7A', lineHeight: 22 }}>{m.notes}</Text>
                  </View>
                ) : null}
              </View>
            )}

            {detailTab === 'performance' && (
              <View style={St.detailSection}>
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <Text style={St.detailSectionTitle}>Performance</Text>
                  <TouchableOpacity onPress={() => openPerfUpdate(m)}
                    style={{ backgroundColor: '#7B1FA2', borderRadius: 10, paddingHorizontal: 12, paddingVertical: 5,
                      flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                    <Ionicons name="pencil" size={12} color="#fff" />
                    <Text style={{ fontSize: 12, color: '#fff', fontWeight: '700' }}>Update Score</Text>
                  </TouchableOpacity>
                </View>
                {perf > 0 ? (
                  <>
                    <View style={{ alignItems: 'center', paddingVertical: 16 }}>
                      <Text style={{ fontSize: 52, fontWeight: '900', color: perfColor(perf) }}>{perf}%</Text>
                      <Text style={{ fontSize: 13, color: '#90A4AE', marginTop: 4 }}>Performance Score</Text>
                      <View style={{ backgroundColor: perfColor(perf) + '20', borderRadius: 12, paddingHorizontal: 14, paddingVertical: 4, marginTop: 6 }}>
                        <Text style={{ fontSize: 12, color: perfColor(perf), fontWeight: '700' }}>
                          <Text>{perf >= 90 ? 'Excellent — Top Performer' : perf >= 70 ? 'Good — On Track' : perf >= 50 ? 'Average — Needs Improvement' : 'Below Average — Action Required'}</Text>
                        </Text>
                      </View>
                    </View>
                    <PerfBar value={perf} height={12} />
                    {m.target > 0 && m.revenue != null && (
                      <View style={{ marginTop: 20 }}>
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 }}>
                          <Text style={{ fontSize: 13, color: '#546E7A' }}>Target Achievement</Text>
                          <Text style={{ fontSize: 13, fontWeight: '700', color: '#0e4e96' }}>
                            {Math.round((m.revenue / m.target) * 100)}%
                          </Text>
                        </View>
                        <View style={{ height: 10, backgroundColor: '#ECEFF1', borderRadius: 5, overflow: 'hidden' }}>
                          <View style={{ width: `${Math.min((m.revenue / m.target) * 100, 100)}%`, height: '100%', backgroundColor: '#0e4e96', borderRadius: 5 }} />
                        </View>
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 6 }}>
                          <Text style={{ fontSize: 11, color: '#90A4AE' }}>Revenue: {fmtCurrency(m.revenue)}</Text>
                          <Text style={{ fontSize: 11, color: '#90A4AE' }}>Target: {fmtCurrency(m.target)}</Text>
                        </View>
                      </View>
                    )}
                  </>
                ) : (
                  <EmptyState icon="bar-chart-outline" title="No performance data" sub="Performance metrics will appear here once recorded" />
                )}
              </View>
            )}

            {detailTab === 'skills' && (
              <View>
                {skills.length > 0 && (
                  <View style={St.detailSection}>
                    <Text style={St.detailSectionTitle}>Skills</Text>
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                      {skills.map((s, i) => (
                        <View key={i} style={[St.chip, { backgroundColor: '#E3F2FD', borderColor: '#BBDEFB' }]}>
                          <Text style={[St.chipText, { color: '#0e4e96' }]}>{s}</Text>
                        </View>
                      ))}
                    </View>
                  </View>
                )}
                {certs.length > 0 && (
                  <View style={St.detailSection}>
                    <Text style={St.detailSectionTitle}>Certifications</Text>
                    {certs.map((c, i) => (
                      <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 6 }}>
                        <Ionicons name="ribbon-outline" size={16} color="#F57C00" />
                        <Text style={{ fontSize: 14, color: '#37474F' }}>{c}</Text>
                      </View>
                    ))}
                  </View>
                )}
                {langs.length > 0 && (
                  <View style={St.detailSection}>
                    <Text style={St.detailSectionTitle}>Languages</Text>
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                      {langs.map((l, i) => (
                        <View key={i} style={[St.chip, { backgroundColor: '#F3E5F5', borderColor: '#E1BEE7' }]}>
                          <Text style={[St.chipText, { color: '#6A1B9A' }]}>{l}</Text>
                        </View>
                      ))}
                    </View>
                  </View>
                )}
              </View>
            )}

            {detailTab === 'contact' && (
              <View style={St.detailSection}>
                <Text style={St.detailSectionTitle}>Contact</Text>
                <InfoRow icon="mail-outline"  label="Email" value={m.email}
                  onPress={() => m.email && Linking.openURL(`mailto:${m.email}`)} />
                <InfoRow icon="call-outline"  label="Phone" value={m.phone}
                  onPress={() => m.phone && Linking.openURL(`tel:${m.phone}`)} />
                <View style={{ flexDirection: 'row', gap: 10, marginTop: 16 }}>
                  {m.email && (
                    <TouchableOpacity style={[St.actionBtn, { flex: 1, backgroundColor: '#E3F2FD' }]}
                      onPress={() => Linking.openURL(`mailto:${m.email}`)}>
                      <Ionicons name="mail" size={18} color="#0e4e96" />
                      <Text style={[St.actionBtnText, { color: '#0e4e96' }]}>Email</Text>
                    </TouchableOpacity>
                  )}
                  {m.phone && (
                    <TouchableOpacity style={[St.actionBtn, { flex: 1, backgroundColor: '#E8F5E9' }]}
                      onPress={() => Linking.openURL(`tel:${m.phone}`)}>
                      <Ionicons name="call" size={18} color="#388E3C" />
                      <Text style={[St.actionBtnText, { color: '#388E3C' }]}>Call</Text>
                    </TouchableOpacity>
                  )}
                  <TouchableOpacity style={[St.actionBtn, { flex: 1, backgroundColor: '#FFF3E0' }]}
                    onPress={() => navigation.navigate('BusinessVideoConferenceScreen', { participantName: m.name, companyId })}>
                    <Ionicons name="videocam" size={18} color="#F57C00" />
                    <Text style={[St.actionBtnText, { color: '#F57C00' }]}>Video</Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}

            {/* Always-visible action strip */}
            <View style={[St.detailSection, { marginBottom: 30 }]}>
              <Text style={St.detailSectionTitle}>Actions</Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                {/* Integration chips */}
                <TouchableOpacity style={[St.actionChip, { borderColor: '#1565C0', backgroundColor: '#E3F2FD' }]}
                  onPress={() => { setDetailModal(false); navigation.navigate('BusinessUserProfileScreen', { prefillUserId: m.user_id || m.id, prefillName: m.name }); }}>
                  <Ionicons name="person-circle-outline" size={16} color="#0e4e96" />
                  <Text style={[St.actionChipText, { color: '#0e4e96' }]}>Full Profile</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[St.actionChip, { borderColor: '#F57C00', backgroundColor: '#FFF3E0' }]}
                  onPress={() => { setDetailModal(false); navigation.navigate('BusinessVideoConferenceScreen', { participantName: m.name, companyId }); }}>
                  <Ionicons name="videocam" size={16} color="#F57C00" />
                  <Text style={[St.actionChipText, { color: '#F57C00' }]}>Instant Call</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[St.actionChip, { borderColor: '#7B1FA2' }]}
                  onPress={() => { setDetailModal(false); openPerfUpdate(m); }}>
                  <Ionicons name="trending-up-outline" size={16} color="#7B1FA2" />
                  <Text style={[St.actionChipText, { color: '#7B1FA2' }]}>Update Perf</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[St.actionChip, { borderColor: '#D84315' }]}
                  onPress={() => { setDetailModal(false); openNoteAdd(m); }}>
                  <Ionicons name="document-text-outline" size={16} color="#D84315" />
                  <Text style={[St.actionChipText, { color: '#D84315' }]}>Add Note</Text>
                </TouchableOpacity>
                <TouchableOpacity style={St.actionChip} onPress={() => toggleStatus(m)}>
                  <Ionicons name={m.status === 'active' ? 'pause-circle-outline' : 'play-circle-outline'} size={16} color="#0e4e96" />
                  <Text style={St.actionChipText}>{m.status === 'active' ? 'Set Inactive' : 'Set Active'}</Text>
                </TouchableOpacity>
                <TouchableOpacity style={St.actionChip}
                  onPress={() => Share.share({ message: `${m.name}\n${m.role || ''}\n${m.department || ''}\n${m.email || ''}` })}>
                  <Ionicons name="share-outline" size={16} color="#0e4e96" />
                  <Text style={St.actionChipText}>Share Profile</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[St.actionChip, { borderColor: '#388E3C' }]}
                  onPress={() => openAddMeeting()}>
                  <Ionicons name="videocam-outline" size={16} color="#388E3C" />
                  <Text style={[St.actionChipText, { color: '#388E3C' }]}>Schedule Meeting</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[St.actionChip, { borderColor: '#D32F2F' }]} onPress={() => deleteMember(m)}>
                  <Ionicons name="person-remove-outline" size={16} color="#D32F2F" />
                  <Text style={[St.actionChipText, { color: '#D32F2F' }]}>Remove</Text>
                </TouchableOpacity>
              </View>
            </View>
          </ScrollView>
        </SafeAreaView>
      </Modal>
    );
  };

  // ── Add/Edit Member Modal ────────────────────────────────────────────────────
  const renderMemberModal = () => (
    <Modal visible={memberModal} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={{ flex: 1, backgroundColor: '#F5F7FA' }}>
        <View style={St.modalHeader}>
          <TouchableOpacity onPress={() => setMemberModal(false)}><Text style={St.modalCancel}>Cancel</Text></TouchableOpacity>
          <Text style={St.modalTitle}>{editMember ? 'Edit Member' : 'Add Member'}</Text>
          <TouchableOpacity onPress={saveMember} disabled={saving}>
            {saving ? <ActivityIndicator size="small" color="#0e4e96" /> : <Text style={St.modalSave}>Save</Text>}
          </TouchableOpacity>
        </View>
        <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
          <ScrollView style={{ flex: 1, padding: 16 }} keyboardShouldPersistTaps="handled">
            <Text style={St.formSection}>Basic Information</Text>
            <FieldInput label="Full Name" value={mf.name} onChange={v => setMf(p => ({ ...p, name: v }))} required />
            <FieldInput label="Job Title / Role" value={mf.role} onChange={v => setMf(p => ({ ...p, role: v }))} placeholder="e.g. Senior Account Manager" />

            <Text style={St.formSection}>Organisation</Text>
            <Text style={St.fieldLabel}>Department</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 14 }}
              contentContainerStyle={{ gap: 7 }}>
              {departments.map(d => (
                <Chip key={d.id} label={d.name} active={mf.department === d.name} color={d.color}
                  onPress={() => setMf(p => ({ ...p, department: mf.department === d.name ? '' : d.name }))} />
              ))}
              <TouchableOpacity onPress={() => { setMemberModal(false); openAddDept(); }}
                style={[St.chip, { borderStyle: 'dashed' }]}>
                <Text style={St.chipText}>+ New Dept</Text>
              </TouchableOpacity>
            </ScrollView>
            <FieldInput label="Manager" value={mf.manager} onChange={v => setMf(p => ({ ...p, manager: v }))} placeholder="Reporting manager's name" />
            <FieldInput label="Territory / Region" value={mf.territory} onChange={v => setMf(p => ({ ...p, territory: v }))} placeholder="e.g. North England" />

            <Text style={St.formSection}>Contract & Status</Text>
            <Text style={St.fieldLabel}>Contract Type</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 7, marginBottom: 14 }}>
              {CONTRACT_TYPES.map(c => <Chip key={c} label={c} active={mf.contract_type === c} onPress={() => setMf(p => ({ ...p, contract_type: c }))} />)}
            </View>
            <Text style={St.fieldLabel}>Status</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 7, marginBottom: 14 }}>
              {STATUS_LIST.map(s => <Chip key={s} label={statusMeta(s).label} active={mf.status === s} color={statusMeta(s).fg} onPress={() => setMf(p => ({ ...p, status: s }))} />)}
            </View>
            <FieldInput label="Join Date" value={mf.join_date} onChange={v => setMf(p => ({ ...p, join_date: v }))} placeholder="YYYY-MM-DD" />
            <FieldInput label="Annual Salary (£)" value={mf.salary} onChange={v => setMf(p => ({ ...p, salary: v }))} numeric placeholder="e.g. 45000" />
            <FieldInput label="Revenue Target (£)" value={mf.target} onChange={v => setMf(p => ({ ...p, target: v }))} numeric placeholder="e.g. 100000" />

            <Text style={St.formSection}>Contact Details</Text>
            <FieldInput label="Email" value={mf.email} onChange={v => setMf(p => ({ ...p, email: v }))} required placeholder="name@company.com" />
            <FieldInput label="Phone" value={mf.phone} onChange={v => setMf(p => ({ ...p, phone: v }))} placeholder="+44 7700 000000" />

            <Text style={St.formSection}>Skills & Qualifications</Text>
            <FieldInput label="Skills" value={mf.skills} onChange={v => setMf(p => ({ ...p, skills: v }))}
              placeholder="e.g. React, Python, Project Management" info="comma separated" />
            <FieldInput label="Certifications" value={mf.certifications} onChange={v => setMf(p => ({ ...p, certifications: v }))}
              placeholder="e.g. PMP, AWS Solutions Architect" info="comma separated" />
            <FieldInput label="Languages" value={mf.languages} onChange={v => setMf(p => ({ ...p, languages: v }))}
              placeholder="e.g. English, French" info="comma separated" />

            <Text style={St.formSection}>Additional Notes</Text>
            <FieldInput label="Notes" value={mf.notes} onChange={v => setMf(p => ({ ...p, notes: v }))} multiline />
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
  );

  // ── Meeting Modal ────────────────────────────────────────────────────────────
  const renderMeetingModal = () => (
    <Modal visible={meetModal} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={{ flex: 1, backgroundColor: '#F5F7FA' }}>
        <View style={St.modalHeader}>
          <TouchableOpacity onPress={() => setMeetModal(false)}><Text style={St.modalCancel}>Cancel</Text></TouchableOpacity>
          <Text style={St.modalTitle}>{editMeeting ? 'Edit Meeting' : 'Schedule Meeting'}</Text>
          <TouchableOpacity onPress={saveMeeting} disabled={saving}>
            {saving ? <ActivityIndicator size="small" color="#0e4e96" /> : <Text style={St.modalSave}>Save</Text>}
          </TouchableOpacity>
        </View>
        <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
          <ScrollView style={{ flex: 1, padding: 16 }} keyboardShouldPersistTaps="handled">
            <Text style={St.formSection}>Meeting Details</Text>
            <FieldInput label="Meeting Title" value={mtf.title} onChange={v => setMtf(p => ({ ...p, title: v }))}
              required placeholder="e.g. Weekly Team Standup" />
            <Text style={St.fieldLabel}>Type</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 7, marginBottom: 14 }}>
              {MEETING_TYPES.map(t => <Chip key={t} label={t.charAt(0).toUpperCase() + t.slice(1)} active={mtf.type === t}
                onPress={() => setMtf(p => ({ ...p, type: t }))} />)}
            </View>

            <Text style={St.formSection}>Schedule</Text>
            <FieldInput label="Date" value={mtf.meeting_date} onChange={v => setMtf(p => ({ ...p, meeting_date: v }))} placeholder="YYYY-MM-DD" />
            <View style={{ flexDirection: 'row', gap: 12 }}>
              <View style={{ flex: 1 }}>
                <FieldInput label="Start Time" value={mtf.time} onChange={v => setMtf(p => ({ ...p, time: v }))} placeholder="09:00" />
              </View>
              <View style={{ flex: 1 }}>
                <FieldInput label="Duration (min)" value={mtf.duration_minutes} onChange={v => setMtf(p => ({ ...p, duration_minutes: v }))} numeric placeholder="60" />
              </View>
            </View>
            <FieldInput label="Location / Link" value={mtf.location} onChange={v => setMtf(p => ({ ...p, location: v }))}
              placeholder="Room A / https://zoom.us/..." />

            <Text style={St.formSection}>Content</Text>
            <FieldInput label="Agenda" value={mtf.agenda} onChange={v => setMtf(p => ({ ...p, agenda: v }))} multiline placeholder="Topics to discuss..." />
            <FieldInput label="Notes" value={mtf.notes} onChange={v => setMtf(p => ({ ...p, notes: v }))} multiline placeholder="Additional information..." />

            <Text style={St.formSection}>Participants</Text>
            <Text style={{ fontSize: 12, color: '#90A4AE', marginBottom: 10 }}>{meetParticipants.length} selected</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
              {members.map(m => {
                const sel = meetParticipants.includes(m.id);
                return (
                  <TouchableOpacity key={m.id}
                    style={[St.chip, sel && { backgroundColor: '#0e4e96', borderColor: '#0e4e96' }]}
                    onPress={() => setMeetParticipants(p => p.includes(m.id) ? p.filter(x => x !== m.id) : [...p, m.id])}>
                    <Text style={[St.chipText, sel && { color: '#fff' }]}>{m.name}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
  );

  // ── Department Modal ─────────────────────────────────────────────────────────
  const renderDeptModal = () => (
    <Modal visible={deptModal} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={{ flex: 1, backgroundColor: '#F5F7FA' }}>
        <View style={St.modalHeader}>
          <TouchableOpacity onPress={() => setDeptModal(false)}><Text style={St.modalCancel}>Cancel</Text></TouchableOpacity>
          <Text style={St.modalTitle}>{editDept ? 'Edit Department' : 'Add Department'}</Text>
          <TouchableOpacity onPress={saveDept} disabled={saving}>
            {saving ? <ActivityIndicator size="small" color="#0e4e96" /> : <Text style={St.modalSave}>Save</Text>}
          </TouchableOpacity>
        </View>
        <ScrollView style={{ flex: 1, padding: 16 }}>
          <FieldInput label="Department Name" value={df.name} onChange={v => setDf(p => ({ ...p, name: v }))} required />
          <FieldInput label="Department Head" value={df.head} onChange={v => setDf(p => ({ ...p, head: v }))} placeholder="Full name" />
          <FieldInput label="Description" value={df.description} onChange={v => setDf(p => ({ ...p, description: v }))} multiline />
          <FieldInput label="Annual Budget (£)" value={df.budget} onChange={v => setDf(p => ({ ...p, budget: v }))} numeric placeholder="500000" />
          <Text style={St.fieldLabel}>Colour</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 20 }}>
            {PALETTE.map(c => (
              <TouchableOpacity key={c} onPress={() => setDf(p => ({ ...p, color: c }))}>
                <View style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: c,
                  borderWidth: df.color === c ? 3 : 0, borderColor: '#222',
                  shadowColor: c, shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.5, shadowRadius: 4, elevation: 4 }} />
              </TouchableOpacity>
            ))}
          </View>
          <View style={[St.deptDot, { width: 56, height: 56, borderRadius: 28, backgroundColor: df.color, alignSelf: 'center', marginBottom: 8 }]} />
          <Text style={{ textAlign: 'center', fontSize: 12, color: '#90A4AE' }}>Preview colour</Text>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  // ── Bulk Actions Modal ───────────────────────────────────────────────────────
  const renderBulkModal = () => (
    <Modal visible={bulkModal} transparent animationType="fade">
      <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' }}>
        <View style={{ backgroundColor: '#fff', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 24 }}>
          <Text style={{ fontSize: 16, fontWeight: '700', color: '#37474F', marginBottom: 4 }}>
            Bulk Actions
          </Text>
          <Text style={{ fontSize: 13, color: '#90A4AE', marginBottom: 20 }}>
            {selected.size} member{selected.size !== 1 ? 's' : ''} selected
          </Text>
          <View style={{ gap: 10 }}>
            {STATUS_LIST.map(s => (
              <TouchableOpacity key={s} style={[St.bulkBtn, { backgroundColor: statusMeta(s).bg }]}
                onPress={() => bulkSetStatus(s)} disabled={saving}>
                <Text style={[St.bulkBtnText, { color: statusMeta(s).fg }]}>Set as {statusMeta(s).label}</Text>
              </TouchableOpacity>
            ))}
            <TouchableOpacity style={[St.bulkBtn, { backgroundColor: '#E3F2FD' }]} onPress={bulkExport}>
              <Text style={[St.bulkBtnText, { color: '#0e4e96' }]}>Export Selected</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[St.bulkBtn, { backgroundColor: '#FFEBEE' }]} onPress={bulkDelete} disabled={saving}>
              <Text style={[St.bulkBtnText, { color: '#D32F2F' }]}>Delete Selected</Text>
            </TouchableOpacity>
          </View>
          <TouchableOpacity onPress={() => setBulkModal(false)} style={{ marginTop: 16, alignItems: 'center' }}>
            <Text style={{ fontSize: 15, color: '#607D8B', fontWeight: '600' }}>Cancel</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  // ── Performance Update Modal ──────────────────────────────────────────────────
  const renderPerfModal = () => (
    <Modal visible={perfModal} transparent animationType="fade">
      <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', justifyContent: 'center', alignItems: 'center', padding: 24 }}>
        <View style={{ backgroundColor: '#fff', borderRadius: 20, padding: 24, width: '100%' }}>
          <Text style={{ fontSize: 16, fontWeight: '800', color: '#263238', marginBottom: 4 }}>
            Update Performance Score
          </Text>
          <Text style={{ fontSize: 13, color: '#78909C', marginBottom: 16 }}>
            {perfTarget?.name}
          </Text>
          {/* Score slider via input */}
          <Text style={{ fontSize: 13, fontWeight: '600', color: '#546E7A', marginBottom: 6 }}>Score (0–100)</Text>
          <TextInput
            style={[St.field, { fontSize: 28, fontWeight: '900', textAlign: 'center', color: perfColor(parseInt(perfValue) || 0) }]}
            value={perfValue}
            onChangeText={v => setPerfValue(v.replace(/[^0-9]/g, '').slice(0, 3))}
            keyboardType="numeric"
            maxLength={3}
            autoFocus
          />
          {/* Quick preset buttons */}
          <View style={{ flexDirection: 'row', gap: 8, marginTop: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            {[25, 50, 60, 70, 80, 90, 100].map(v => (
              <TouchableOpacity key={v} onPress={() => setPerfValue(String(v))}
                style={{ backgroundColor: perfColor(v) + '20', borderRadius: 10, paddingHorizontal: 12, paddingVertical: 6,
                  borderWidth: 1.5, borderColor: parseInt(perfValue) === v ? perfColor(v) : 'transparent' }}>
                <Text style={{ fontSize: 13, fontWeight: '700', color: perfColor(v) }}>{v}%</Text>
              </TouchableOpacity>
            ))}
          </View>
          <View style={{ flexDirection: 'row', gap: 10 }}>
            <TouchableOpacity style={{ flex: 1, padding: 13, borderRadius: 12, backgroundColor: '#F5F7FA', alignItems: 'center' }}
              onPress={() => setPerfModal(false)}>
              <Text style={{ color: '#78909C', fontWeight: '700' }}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={{ flex: 1, padding: 13, borderRadius: 12, backgroundColor: '#7B1FA2', alignItems: 'center' }}
              onPress={savePerfUpdate} disabled={saving}>
              {saving ? <ActivityIndicator color="#fff" size={16} />
                : <Text style={{ color: '#fff', fontWeight: '800' }}>Save Score</Text>}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  // ── Note Modal ────────────────────────────────────────────────────────────────
  const renderNoteModal = () => (
    <Modal visible={noteModal} transparent animationType="slide">
      <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' }}>
        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={{ backgroundColor: '#fff', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 24 }}>
            <Text style={{ fontSize: 16, fontWeight: '800', color: '#263238', marginBottom: 4 }}>
              Notes — {noteTarget?.name}
            </Text>
            <TextInput
              style={[St.field, { height: 120, textAlignVertical: 'top', paddingTop: 12, marginTop: 12, marginBottom: 16 }]}
              value={noteText}
              onChangeText={setNoteText}
              placeholder="Add notes about this team member…"
              placeholderTextColor="#B0BEC5"
              multiline
              autoFocus
            />
            <View style={{ flexDirection: 'row', gap: 10 }}>
              <TouchableOpacity style={{ flex: 1, padding: 13, borderRadius: 12, backgroundColor: '#F5F7FA', alignItems: 'center' }}
                onPress={() => setNoteModal(false)}>
                <Text style={{ color: '#78909C', fontWeight: '700' }}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={{ flex: 1, padding: 13, borderRadius: 12, backgroundColor: '#D84315', alignItems: 'center' }}
                onPress={saveNote} disabled={saving}>
                {saving ? <ActivityIndicator color="#fff" size={16} />
                  : <Text style={{ color: '#fff', fontWeight: '800' }}>Save Note</Text>}
              </TouchableOpacity>
            </View>
          </View>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );

  // ── Main render ──────────────────────────────────────────────────────────────
  const TABS = [
    { id: 'overview',    label: 'Overview',    icon: 'home-outline'      },
    { id: 'members',     label: 'Members',     icon: 'people-outline'    },
    { id: 'meetings',    label: 'Meetings',    icon: 'videocam-outline'  },
    { id: 'departments', label: 'Departments', icon: 'business-outline'  },
  ];

  if (loading) {
    return (
      <SafeAreaView style={St.loader}>
        <ActivityIndicator size="large" color="#0e4e96" />
        <Text style={{ color: '#78909C', marginTop: 14, fontSize: 14 }}>Loading team…</Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={St.container}>
      {/* Header */}
      <View style={St.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={St.headerIconBtn}>
          <Ionicons name="arrow-back" size={22} color="#0e4e96" />
        </TouchableOpacity>
        <View style={{ flex: 1 }}>
          {companyLogo ? (
                <TouchableOpacity onPress={() => navigation?.navigate('CompanyProfileScreen')}>
                  <Image source={{ uri: companyLogo || undefined }} style={{ width: 34, height: 34, borderRadius: 8 }} resizeMode="cover" />
                </TouchableOpacity>
              ) : null}
              <Text style={St.headerTitle}>Team Management</Text>
          <Text style={St.headerSub}>{activeMembers} active · {totalMembers} total</Text>
        </View>
        {bulkMode && selected.size > 0 ? (
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <TouchableOpacity style={[St.headerIconBtn, { backgroundColor: '#FFF3E0' }]} onPress={() => setBulkModal(true)}>
              <Ionicons name="ellipsis-horizontal" size={20} color="#F57C00" />
            </TouchableOpacity>
            <TouchableOpacity style={[St.headerIconBtn, { backgroundColor: '#FFEBEE' }]} onPress={clearSelect}>
              <Ionicons name="close" size={20} color="#D32F2F" />
            </TouchableOpacity>
          </View>
        ) : (
          <View style={{ flexDirection: 'row', gap: 6 }}>
            <TouchableOpacity onPress={() => navigation.navigate('TeamAnalyticsScreen', { members, departments, companyId })} style={St.headerIconBtn}>
              <Ionicons name="bar-chart-outline" size={22} color="#0e4e96" />
            </TouchableOpacity>
            <TouchableOpacity onPress={() => navigation.navigate('BusinessVideoConferenceScreen', { companyId })} style={St.headerIconBtn}>
              <Ionicons name="videocam-outline" size={22} color="#0e4e96" />
            </TouchableOpacity>
            <TouchableOpacity onPress={() => navigation.navigate('CompanyProfileScreen', { companyProfile })} style={St.headerIconBtn}>
              <Ionicons name="business-outline" size={22} color="#0e4e96" />
            </TouchableOpacity>
          </View>
        )}
      </View>

      {/* Tabs */}
      <View style={St.tabBar}>
        {TABS.map(t => (
          <TouchableOpacity key={t.id} style={[St.tab, activeTab === t.id && St.tabActive]}
            onPress={() => setActiveTab(t.id)}>
            <Ionicons name={t.icon} size={17} color={activeTab === t.id ? '#0e4e96' : '#90A4AE'} />
            <Text style={[St.tabLabel, activeTab === t.id && St.tabLabelActive]}>{t.label}</Text>
          </TouchableOpacity>
        ))}
      </View>

      <View style={{ flex: 1 }}>
        {activeTab === 'overview'    && renderOverview()}
        {activeTab === 'members'     && renderMembers()}
        {activeTab === 'meetings'    && renderMeetings()}
        {activeTab === 'departments' && renderDepartments()}
      </View>

      {renderDetailModal()}
      {renderMemberModal()}
      {renderMeetingModal()}
      {renderDeptModal()}
      {renderBulkModal()}
      {renderPerfModal()}
      {renderNoteModal()}
    </SafeAreaView>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────
const St = StyleSheet.create({
  container:    { flex: 1, backgroundColor: '#F5F7FA' },
  loader:       { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: '#F5F7FA' },

  header:       { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 10,
    backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#ECEFF1', gap: 10 },
  headerTitle:  { fontSize: 17, fontWeight: '800', color: '#263238' },
  headerSub:    { fontSize: 12, color: '#78909C', marginTop: 1 },
  headerIconBtn:{ width: 36, height: 36, borderRadius: 18, backgroundColor: '#F5F7FA', alignItems: 'center', justifyContent: 'center' },

  tabBar:       { flexDirection: 'row', backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#ECEFF1' },
  tab:          { flex: 1, alignItems: 'center', paddingVertical: 9, gap: 3 },
  tabActive:    { borderBottomWidth: 2, borderBottomColor: '#0e4e96' },
  tabLabel:     { fontSize: 10, color: '#90A4AE', fontWeight: '600' },
  tabLabelActive: { color: '#0e4e96', fontWeight: '800' },

  hero:         { margin: 14, borderRadius: 18, padding: 20 },
  heroTitle:    { color: '#fff', fontSize: 18, fontWeight: '800' },
  heroSub:      { color: 'rgba(255,255,255,0.7)', fontSize: 12, marginTop: 2, marginBottom: 18 },
  heroGrid:     { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 16 },
  heroStat:     { width: (width - 80) / 3, alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.12)',
    borderRadius: 12, paddingVertical: 10, gap: 4 },
  heroStatNum:  { color: '#fff', fontSize: 20, fontWeight: '900' },
  heroStatLabel:{ color: 'rgba(255,255,255,0.7)', fontSize: 10, fontWeight: '600' },
  heroPerfRow:  { flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: 10, paddingHorizontal: 14, paddingVertical: 10 },

  card:         { backgroundColor: '#fff', marginHorizontal: 14, marginBottom: 12, borderRadius: 16, padding: 16 },
  sectionTitle: { fontSize: 15, fontWeight: '800', color: '#263238' },
  countPill:    { backgroundColor: '#E3F2FD', borderRadius: 10, paddingHorizontal: 8, paddingVertical: 2 },
  countPillText:{ fontSize: 12, color: '#0e4e96', fontWeight: '700' },

  qaBtn:        { borderRadius: 14, paddingVertical: 18, alignItems: 'center', justifyContent: 'center', gap: 8 },
  qaBtnText:    { color: '#fff', fontSize: 12, fontWeight: '700', textAlign: 'center' },

  statusRow:    { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  statusDot:    { width: 10, height: 10, borderRadius: 5, marginRight: 10 },
  statusLabel:  { fontSize: 13, color: '#546E7A', width: 80 },

  deptRow:      { flexDirection: 'row', alignItems: 'center', paddingVertical: 10 },
  deptDot:      { width: 12, height: 12, borderRadius: 6, marginRight: 12, flexShrink: 0 },
  deptName:     { fontSize: 14, fontWeight: '700', color: '#263238' },
  deptHead:     { fontSize: 12, color: '#78909C', marginTop: 1 },
  deptCount:    { fontSize: 14, fontWeight: '800', color: '#0e4e96', marginRight: 8 },

  deptCard:     { backgroundColor: '#fff', borderRadius: 16, padding: 16, marginBottom: 12,
    borderLeftWidth: 5, shadowColor: '#000', shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08, shadowRadius: 8, elevation: 3 },
  deptCardIcon: { width: 42, height: 42, borderRadius: 21, alignItems: 'center', justifyContent: 'center' },
  deptCardName: { fontSize: 16, fontWeight: '800', color: '#263238' },

  addDeptBtnRow:{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8,
    borderWidth: 1.5, borderColor: '#0e4e96', borderStyle: 'dashed', borderRadius: 14,
    paddingVertical: 14, marginBottom: 12 },

  kpiBox:       { backgroundColor: '#F5F7FA', borderRadius: 12, padding: 12, alignItems: 'center' },
  kpiBoxLabel:  { fontSize: 11, color: '#90A4AE', fontWeight: '600', marginBottom: 4 },
  kpiBoxValue:  { fontSize: 16, fontWeight: '800' },

  topRow:       { flexDirection: 'row', alignItems: 'center', paddingVertical: 10,
    borderBottomWidth: 1, borderBottomColor: '#F5F7FA' },
  rankText:     { fontSize: 13, fontWeight: '800', width: 28 },
  topName:      { fontSize: 14, fontWeight: '700', color: '#263238', marginBottom: 2 },
  topRole:      { fontSize: 12, color: '#78909C', marginBottom: 5 },

  meetSnip:     { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, gap: 10 },
  meetDot:      { width: 10, height: 10, borderRadius: 5, flexShrink: 0 },
  meetSnipTitle:{ fontSize: 13, fontWeight: '700', color: '#263238' },
  meetSnipMeta: { fontSize: 11, color: '#78909C', marginTop: 2 },
  joinPill:     { backgroundColor: '#0e4e96', borderRadius: 16, paddingHorizontal: 12, paddingVertical: 6 },
  joinPillText: { color: '#fff', fontSize: 12, fontWeight: '700' },

  meetCard:     { backgroundColor: '#fff', borderRadius: 16, padding: 16, marginBottom: 10,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.07, shadowRadius: 6, elevation: 2 },
  meetIconWrap: { width: 46, height: 46, borderRadius: 23, alignItems: 'center', justifyContent: 'center', marginRight: 12, flexShrink: 0 },
  meetTitle:    { fontSize: 15, fontWeight: '700', color: '#263238' },
  meetMeta:     { fontSize: 12, color: '#78909C' },
  meetAgenda:   { fontSize: 12, color: '#546E7A', marginTop: 6, lineHeight: 17 },
  meetActions:  { flexDirection: 'row', alignItems: 'center', gap: 8 },
  joinBtn:      { flex: 1, backgroundColor: '#0e4e96', borderRadius: 10, paddingVertical: 9,
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6 },
  joinBtnText:  { color: '#fff', fontSize: 13, fontWeight: '700' },

  searchBar:    { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 8,
    backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#ECEFF1', gap: 8 },
  searchBox:    { flex: 1, flexDirection: 'row', alignItems: 'center', backgroundColor: '#F5F7FA',
    borderRadius: 12, paddingHorizontal: 12, paddingVertical: 9, gap: 8 },
  searchInput:  { flex: 1, fontSize: 14, color: '#263238' },
  addBtn:       { width: 38, height: 38, borderRadius: 19, backgroundColor: '#0e4e96', alignItems: 'center', justifyContent: 'center' },
  iconBtn:      { width: 34, height: 34, borderRadius: 17, backgroundColor: '#F5F7FA', alignItems: 'center', justifyContent: 'center' },

  sortRow:      { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff',
    borderBottomWidth: 1, borderBottomColor: '#ECEFF1', paddingVertical: 6 },
  sortBtn:      { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16,
    backgroundColor: '#F5F7FA', borderWidth: 1, borderColor: '#ECEFF1' },
  sortBtnActive:{ backgroundColor: '#0e4e96', borderColor: '#0e4e96' },
  sortBtnText:  { fontSize: 12, color: '#607D8B', fontWeight: '600' },
  sortBtnTextActive: { color: '#fff' },

  filterPanel:  { backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#ECEFF1' },

  chip:         { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 20, backgroundColor: '#F5F7FA', borderWidth: 1, borderColor: '#ECEFF1' },
  chipText:     { fontSize: 12, color: '#546E7A', fontWeight: '600' },

  memberCard:   { backgroundColor: '#fff', borderRadius: 16, padding: 14, marginBottom: 8,
    borderWidth: 1, borderColor: 'transparent',
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.06, shadowRadius: 5, elevation: 2 },
  mName:        { fontSize: 15, fontWeight: '800', color: '#263238' },
  mRole:        { fontSize: 13, color: '#546E7A', marginTop: 2 },
  mDept:        { fontSize: 12, color: '#90A4AE', marginTop: 1 },
  perfNum:      { fontSize: 15, fontWeight: '800' },

  emptyWrap:    { alignItems: 'center', paddingVertical: 48, paddingHorizontal: 32 },
  emptyIconWrap:{ width: 80, height: 80, borderRadius: 40, backgroundColor: '#ECEFF1',
    alignItems: 'center', justifyContent: 'center', marginBottom: 12 },
  emptyTitle:   { fontSize: 16, fontWeight: '700', color: '#546E7A', textAlign: 'center' },
  emptySub:     { fontSize: 13, color: '#90A4AE', textAlign: 'center', marginTop: 6, lineHeight: 18 },
  emptyBtn:     { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 16,
    backgroundColor: '#0e4e96', borderRadius: 12, paddingHorizontal: 20, paddingVertical: 10 },
  emptyBtnText: { color: '#fff', fontSize: 14, fontWeight: '700' },

  infoRow:      { flexDirection: 'row', alignItems: 'center', paddingVertical: 9,
    borderBottomWidth: 1, borderBottomColor: '#F5F7FA' },
  infoIcon:     { width: 30, height: 30, borderRadius: 15, backgroundColor: '#E3F2FD',
    alignItems: 'center', justifyContent: 'center', marginRight: 12 },
  infoLabel:    { width: 96, fontSize: 12, color: '#90A4AE', fontWeight: '600' },
  infoValue:    { flex: 1, fontSize: 14, color: '#37474F', fontWeight: '500' },

  modalHeader:  { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 16, paddingVertical: 14, backgroundColor: '#fff',
    borderBottomWidth: 1, borderBottomColor: '#ECEFF1' },
  modalTitle:   { flex: 1, fontSize: 16, fontWeight: '800', color: '#263238', textAlign: 'center' },
  modalCancel:  { fontSize: 15, color: '#78909C', minWidth: 60 },
  modalSave:    { fontSize: 15, color: '#0e4e96', fontWeight: '800', minWidth: 60, textAlign: 'right' },

  formSection:  { fontSize: 12, fontWeight: '800', color: '#0e4e96', textTransform: 'uppercase',
    letterSpacing: 1, marginTop: 16, marginBottom: 12 },
  fieldLabel:   { fontSize: 13, fontWeight: '600', color: '#546E7A', marginBottom: 6 },
  field:        { backgroundColor: '#fff', borderWidth: 1, borderColor: '#CFD8DC', borderRadius: 12,
    paddingHorizontal: 14, paddingVertical: 12, fontSize: 14, color: '#263238' },

  detailHero:   { alignItems: 'center', padding: 30, gap: 6 },
  detailName:   { color: '#fff', fontSize: 22, fontWeight: '900', marginTop: 10 },
  detailRole:   { color: 'rgba(255,255,255,0.85)', fontSize: 14 },
  detailSection:{ backgroundColor: '#fff', marginHorizontal: 14, marginTop: 10, borderRadius: 16, padding: 16 },
  detailSectionTitle: { fontSize: 12, fontWeight: '800', color: '#90A4AE', textTransform: 'uppercase',
    letterSpacing: 1, marginBottom: 10 },

  actionBtn:    { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6,
    borderRadius: 12, paddingVertical: 12 },
  actionBtnText:{ fontSize: 13, fontWeight: '700' },
  actionChip:   { flexDirection: 'row', alignItems: 'center', gap: 6, borderWidth: 1.5, borderColor: '#0e4e96',
    borderRadius: 20, paddingHorizontal: 14, paddingVertical: 8 },
  actionChipText:{ color: '#0e4e96', fontSize: 13, fontWeight: '700' },

  bulkBtn:      { borderRadius: 12, paddingVertical: 14, alignItems: 'center', justifyContent: 'center' },
  bulkBtnText:  { fontSize: 15, fontWeight: '700' },

  qaBtnSmall:     { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 4,
    borderRadius: 8, paddingVertical: 7 },
  qaBtnSmallText: { fontSize: 11, fontWeight: '700' },
});